import {
  AI_BACKGROUND_TEXT_COLOR,
  autofixOverlays,
  BACKDROP_TEXT_COLORS,
  overlays,
  specialBackgrounds,
} from '@/helpers/backgroundHelper'
import { addDuoToneBackground, removeDuoToneBackground, updateCTA } from '@/helpers/canvasHelper'
import {
  handleUSToUKEnglish,
  reflowTextAfterScaling,
  reflowTextBasedOnCollisions,
  updateTextObject,
  USkeywords,
} from '@/helpers/textHelper'
import { useBrandStore } from '@/stores/brand'
import { useCanvasStore } from '@/stores/canvas'
import { useEditorStore, type TiltStrategy } from '@/stores/editor'
import { useEnhancementsStore } from '@/stores/enhancement'
import { usePlacementStore } from '@/stores/placement'
import { useCreativeStore } from '@/stores/creative'
import { useProductStore } from '@/stores/product'
import { useAssetStore } from '@/stores/asset'
import type { EnhanceRequest } from '@/types/enhanceRequest'
import type { EnhancingItem } from '@/types/enhancingItem'
import type { ProductWithInfo } from '@/types/product'
import CreativeService from './creativeService'
import { Canvas, Control, FabricImage, FabricObject, Group, loadSVGFromURL, util } from 'fabric'
import { getDominantColor, loadHtmlImage, showGlowLayer } from '@/helpers/imageHelper'
import { getProminentColors, hslToRgb, rgbToHex, rgbToHsl } from '@/helpers/colorHelper'
import { argbFromHex, hexFromArgb, TonalPalette } from '@material/material-color-utilities'
import {
  getLightDirectionsForPlacementType,
  getProductDimensions,
  countAspects,
  determineAvailablePlacements,
  suggestOptimalPlacement,
} from '@/helpers/productPlacementHelper'
import {
  getRandomNumber,
  getContrastColor,
  moveToBottom,
  moveToFront,
  renderChevronIcon,
  hasGoodContrast,
} from '@/helpers/commonHelper'
import { loadPlacements } from '@/helpers/placementHelper'
import { TEXT_TYPE_NAMES, type TextTypeName } from '@/types/textTypeName'

import type { GenerateBgWithOverlayRequest } from '@/types/generateBgWithOverlayRequest'
import { useAppStore } from '@/stores/app'
import { Shadow } from 'fabric'
import {
  syncShadow,
  createGlowShadow,
  createShadow,
  syncContactShadow,
  AMBIENT_SHADOW_COLOR,
  DROP_SHADOW_COLOR,
  syncAIShadow,
  generateAIShadows,
  isFlatBase,
} from '@/helpers/shadowHelper'
import { TILT_ANGLES, TILT_STRATEGIES } from '@/constants/advanceSettings'
import {
  DIRECTIONS,
  PLACEMENT_TYPES,
  PRODUCT_ASPECT_TYPES,
  SPECIAL_PLACEMENTS,
} from '@/constants/positions'
import { getDefaultPlacementArea } from '@/helpers/placementHelper'
import type { Logo } from '@/types/logo'
import {
  getBestContrastingLogo,
  getHighContrastLogos,
  getImageAspectRatio,
} from '@/helpers/logoHelper'
import { discountTypes, squareAspectRatioBuffer } from '@/constants/canvas'
import type { GenerateRequest } from '@/types/generateRequest'
import type { ProductData } from '@/helpers/productPlacementHelper'
import type { backdrop } from '@/types/backdrop'
import type { background } from '@/types/background'
import { isAllFoodCategories } from '@/helpers/productHelper'
import { layerNames } from '@/constants/layers'
import type { AIShadowProperties } from '@/types/aiShadows'
import { useAutofixStore } from '@/stores/autofix'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'

class EditorService {
  private readonly canvasStore: ReturnType<typeof useCanvasStore>
  private readonly editorStore: ReturnType<typeof useEditorStore>
  private readonly placementStore: ReturnType<typeof usePlacementStore>
  private readonly enhancementStore: ReturnType<typeof useEnhancementsStore>
  private readonly brandStore: ReturnType<typeof useBrandStore>
  private readonly appStore: ReturnType<typeof useAppStore>
  private readonly creativeStore: ReturnType<typeof useCreativeStore>
  private readonly productStore: ReturnType<typeof useProductStore>
  private readonly assetStore: ReturnType<typeof useAssetStore>
  private readonly autofixStore: ReturnType<typeof useAutofixStore>
  private readonly adSizeConfigStore: ReturnType<typeof useAdSizeConfigStore>
  private readonly creativeService: CreativeService

  // Constants
  private readonly WHITE = '#ffffff'
  private readonly BLACK = '#000000'
  private readonly specialPlacements = [
    PLACEMENT_TYPES.FRONT,
    PLACEMENT_TYPES.STAIRCASE,
    PLACEMENT_TYPES.LAYER,
  ]
  private readonly specialTexturesIndex = [1, 2]
  private readonly USkeywords = [
    'color',
    'center',
    'fiber',
    'meter',
    'liter',
    'theater',
    'caliber',
    'saber',
    'sabre',
    'sepulcher',
    'defense',
    'license',
    'offense',
    'pretense',
    'vise',
    'analyze',
    'paralyze',
    'tire',
    'organize',
    'recognize',
    'apologize',
    'catalog',
    'dialog',
    'monolog',
    'prolog',
    'analog',
    'decal',
    'fiscal',
    'miscal',
    'recital',
    'tidal',
    'arrival',
    'rival',
    'vial',
    'dial',
    'rial',
    'denial',
    'memorial',
    'burial',
    'trial',
    'trivial',
    'advertise',
    'advise',
    'devise',
    'revise',
    'supervise',
    'surmise',
    'televise',
    'improvise',
    'compromise',
    'enterprise',
    'exercise',
    'merchandise',
    'surprise',
    'despise',
    'disguise',
    'excise',
    'incise',
    'precise',
    'concise',
    'demise',
    'emphasize',
    'realize',
    'specialize',
    'standardize',
    'summarize',
    'symbolize',
    'utilize',
    'visualize',
    'authorize',
    'categorize',
    'characterize',
    'customize',
    'dramatize',
    'emphasize',
    'generalize',
    'globalize',
    'harmonize',
    'humanize',
    'idealize',
    'legalize',
    'localize',
    'maximize',
    'minimize',
    'modernize',
    'normalize',
    'optimize',
    'organize',
    'personalize',
    'popularize',
    'prioritize',
    'rationalize',
    'realize',
    'recognize',
    'regularize',
    'reorganize',
    'revitalize',
    'stabilize',
    'standardize',
    'summarize',
    'symbolize',
    'synchronize',
    'systematize',
    'utilize',
    'visualize',
  ]

  constructor() {
    this.canvasStore = useCanvasStore()
    this.editorStore = useEditorStore()
    this.placementStore = usePlacementStore()
    this.enhancementStore = useEnhancementsStore()
    this.brandStore = useBrandStore()
    this.appStore = useAppStore()
    this.creativeStore = useCreativeStore()
    this.productStore = useProductStore()
    this.assetStore = useAssetStore()
    this.creativeService = new CreativeService()
    this.autofixStore = useAutofixStore()
    this.adSizeConfigStore = useAdSizeConfigStore()
  }

  private lastRandomIndex: number[] = []
  updateCanvasJsonText = (
    text: string,
    layerName: string,
    options?: {
      shouldReflow?: boolean
      color?: string
      discountInfo?: any
    },
  ) => {
    const { shouldReflow = true, color, discountInfo } = options ?? {}
    const fabricObjects = this.canvasStore.fabricCanvas.getObjects()
    const layerObj = fabricObjects.find((obj: any) => obj.layerName === layerName)
    if (!layerObj) return

    // Capture current state for undo
    const previousText = layerObj.text || ''
    const newText = text.trim()

    if (previousText == newText) {
      return
    }

    if (discountInfo) {
      this.canvasStore.setDiscountType(discountInfo.type)
      this.canvasStore.setDiscountVariation(discountInfo.variation)
      this.canvasStore.setDiscountValue(discountInfo.value)
      this.canvasStore.setRawCouponCode(discountInfo.couponCode)
      this.canvasStore.setLastUsedDiscountInfo(discountInfo)
    }

    updateTextObject(layerObj, {
      text: newText,
      previousHeight: layerObj.height,
      fill: color || layerObj.fill,
    })

    // apply bold to discount code
    if (layerName === 'SubHeadline') {
      this.applyBoldToDiscountCode(layerObj)
      this.editorStore.setSubheadline(newText)
    } else if (layerName === 'Headline') {
      this.editorStore.setHeadline(newText)
    }

    if (shouldReflow) {
      // reflow
      this.triggerTextReflow()
    }
    this.canvasStore.fabricCanvas.renderAll()
  }

  applyBoldToDiscountCode = (layerObj: any) => {
    layerObj.styles = []
    if (this.canvasStore.rawCouponCode) {
      const couponCode = this.canvasStore.rawCouponCode.toUpperCase()
      const simulatedTextLines = layerObj.text?.split('\n')
      const lineIndex = simulatedTextLines?.findIndex((line: any) => line.includes(couponCode))
      if (lineIndex != undefined && lineIndex != -1 && simulatedTextLines) {
        const currentLine = simulatedTextLines[lineIndex]
        const startCharIndex = currentLine.indexOf(couponCode)
        const endCharIndex = startCharIndex + couponCode.length - 1
        for (let i = startCharIndex; i <= endCharIndex; i++) {
          layerObj.styles[lineIndex] = layerObj.styles[lineIndex] || {}
          layerObj.styles[lineIndex][i] = { fontWeight: 'bold' }
        }
      }
    }
  }

  updateBackgroundOption = (backgroundOption: number) => {
    this.editorStore.setSelectedBackgroundOption(backgroundOption)
  }

  async addTextColor(newTextColor: string) {
    this.autofixStore.autofixColors.push(newTextColor)
    this.selectNewTextColor(this.autofixStore.autofixColors.length - 1)
  }

  async removeTextColor(index: number) {
    this.autofixStore.autofixColors = this.autofixStore.autofixColors.slice(
      0,
      this.autofixStore.autofixColors.length - 1,
    )
    this.selectNewTextColor(index)
  }

  async addBackDrops(backdrop: backdrop, action: string = 'append') {
    const brandsLength = this.brandStore.selectedBrand?.colours.length ?? 0
    const insertingIndex =
      action === 'prepend' ? 0 : this.editorStore.createdBackDrops.length - brandsLength
    this.editorStore.addBackDrops(backdrop, action, insertingIndex)
    for (let i = 0; i < this.editorStore.specialIndex.length; i++) {
      if (this.editorStore.specialIndex[i].index >= insertingIndex) {
        this.editorStore.specialIndex[i].index += 1
      }
    }
    const newIndex =
      action === 'prepend'
        ? 0
        : action === 'insert'
          ? this.editorStore.createdBackDrops.length - brandsLength - 1
          : this.editorStore.createdBackDrops.length - 1
    await this.selectNewBackDrop(newIndex)
  }

  async addBackGrounds(background: background, action: string = 'prepend') {
    this.editorStore.prependBackGrounds(background)
    const newIndex = action === 'prepend' ? 0 : this.editorStore.createdBackGrounds.length - 1
    await this.selectNewBackground(newIndex)
  }

  async removeBackDrops(index: number) {
    this.editorStore.removeBackDrops(index)
    this.selectNewBackDrop(index)
  }

  async removeBackGrounds(index: number) {
    this.editorStore.removeBackGrounds(index)
    this.selectNewBackground(index)
  }

  async editBackDrop(index: number, backdrop: any) {
    this.editorStore.createdBackDrops[index] = backdrop
    const specialIndex = this.editorStore.specialIndex.find((item) => item.index === index)
    if (specialIndex) {
      if (this.specialTexturesIndex.includes(backdrop.backgroundTextureIndex)) {
        specialIndex.specialIndex = backdrop.backgroundTextureIndex
      } else {
        this.editorStore.specialIndex = this.editorStore.specialIndex.filter(
          (item) => item.index != specialIndex.index,
        )
      }
    }
    await this.selectNewBackDrop(index)
  }

  async editBackGround(index: number, background: any) {
    this.editorStore.createdBackGrounds[index] = background
    await this.selectNewBackground(index)
  }

  prependBackGrounds(background: background) {
    this.editorStore.prependBackGrounds(background)
  }

  // Extracted text reflow logic for reuse
  triggerTextReflow = (fabricCanvas: Canvas | null = null) => {
    const canvas = fabricCanvas || this.canvasStore.fabricCanvas
    const fabricObjects = canvas.getObjects()

    const glowGroups = fabricObjects.filter((obj: any) => obj?.layerName?.includes('glowGroup'))
    if (glowGroups.length > 0) {
      reflowTextAfterScaling(canvas, glowGroups[0])
    } else {
      const products = fabricObjects.filter((obj: any) => obj.layerName?.includes('Product'))
      if (products.length > 0) {
        const sortedProducts = [...products].sort((a, b) => {
          const aLeft = a.getBoundingRect().left
          const bLeft = b.getBoundingRect().left
          return aLeft - bLeft
        })
        reflowTextBasedOnCollisions(canvas, sortedProducts)
      }
    }
  }

  private parseRgbString = (rgbStr: string) => {
    const match = rgbStr.replace(/\s+/g, '').match(/^rgb?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/i)
    if (!match) return null
    const [, r, g, b] = match
    return {
      r: Number(r),
      g: Number(g),
      b: Number(b),
    }
  }

  // Function to get the appropriate Button Text Color
  private getBackdropCTAColor = (textColor: string, backgroundColor: string) => {
    const finalColor = textColor.toLowerCase()
    if (finalColor.toLowerCase() === this.WHITE.toLowerCase()) {
      if (hasGoodContrast(this.WHITE, backgroundColor)) {
        return backgroundColor
      }
      return this.BLACK
    }
    // If already black → safe, just return
    // Default → input color is already fine
    else if (finalColor.toLowerCase() === this.BLACK.toLowerCase()) {
      return this.BLACK
    }
    return textColor.toLowerCase()
  }

  selectNewBackDrop = async (index: number) => {
    try {
      const creativeService = new CreativeService()
      const isGlowShadowSupported =
        this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.FLOAT ||
        this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER
      this.editorStore.selectedBackdrop = '#backdrop' + index
      this.editorStore.selectedBackground = ''
      this.editorStore.selectedBackgroundOption = 1
      const backDrop = this.editorStore.createdBackDrops[index]
      this.editorStore.selectedBackDropIndex = index
      this.editorStore.selectedBackGroundIndex = null
      const backgroundImages = this.placementStore.backgroundImages
      let backgroundColor = JSON.parse(JSON.stringify(backDrop.backgroundColor))
      let textColor = JSON.parse(JSON.stringify(backDrop.textColor))
      if (backgroundColor.startsWith('rgb')) {
        this.brandStore.selectedBackgroundColorType = 1
        const color = this.parseRgbString(backgroundColor)
        if (color) {
          backgroundColor = rgbToHex(color?.r, color?.g, color.b)
        }
      } else {
        this.brandStore.selectedBackgroundColorType = 0
      }
      if (textColor.startsWith('rgb')) {
        this.brandStore.selectedTextColorType = 1
        const color = this.parseRgbString(textColor)
        if (color) {
          textColor = rgbToHex(color?.r, color?.g, color.b)
        }
      } else {
        this.brandStore.selectedTextColorType = 0
      }
      const key = `${backgroundColor}_${backgroundImages[backDrop.backgroundTextureIndex]}`
      const selectedItem = this.enhancementStore.selectedItem
      const enhancingItems = this.enhancementStore.enhancingItems
      const selectedItemData = selectedItem !== null ? enhancingItems[selectedItem] : undefined
      this.placementStore.setSelectedBackgroundImage(
        this.placementStore.backgroundImages[backDrop.backgroundTextureIndex],
      )
      this.placementStore.setSelectedAIBackgroundImage('')
      removeDuoToneBackground(this.canvasStore.fabricCanvas)
      this.startLoader()
      if (selectedItemData?.apply && key in selectedItemData.apply) {
        if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
          await this.updateLogoOnShuffling(false)
        }
        await this.getGlowOfLogo(backDrop)
        await this.addBackgroundImage(this.canvasStore.fabricCanvas, selectedItemData.apply[key])
        if (this.editorStore.isGlowShadowApplied && isGlowShadowSupported) {
          this.createGlowShadow()
        }
        this.stopLoader()
        return
      }
      const itemWithRemoveKey = enhancingItems.find(
        (item: EnhancingItem) => item.remove && key in item.remove,
      )
      if (
        itemWithRemoveKey &&
        backgroundImages[backDrop.backgroundTextureIndex] != specialBackgrounds[0]
      ) {
        if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
          await this.updateLogoOnShuffling(false)
        }
        await this.getGlowOfLogo(backDrop)
        await this.addBackgroundImage(this.canvasStore.fabricCanvas, itemWithRemoveKey.remove[key])
        if (this.editorStore.isGlowShadowApplied && isGlowShadowSupported) {
          this.createGlowShadow()
        }
        this.stopLoader()
        return
      }

      if (backgroundImages[backDrop.backgroundTextureIndex] == specialBackgrounds[0]) {
        this.startLoader()
        if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
          await this.updateLogoOnShuffling(false)
        }
        await this.getGlowOfLogo(backDrop)
        await addDuoToneBackground(
          this.canvasStore.fabricCanvas,
          backgroundColor,
          specialBackgrounds[1],
        )
        if (this.editorStore.isGlowShadowApplied && isGlowShadowSupported) {
          this.createGlowShadow()
        }
        this.stopLoader()
        return
      }
      const effectName = undefined
      const bgColor = backgroundColor
      const baseBgImageUrl = backgroundImages[backDrop.backgroundTextureIndex] || ''
      const requestData: EnhanceRequest = {
        brandName: this.brandStore.selectedBrand?.name || '',
        baseBgImageUrl: baseBgImageUrl,
        currentBgImageUrl: backgroundImages[backDrop.backgroundTextureIndex] || '',
        maskImageUrl: this.placementStore.selectedPlacement?.maskImages['bottomRight'] || '',
        effectName: effectName,
        bgColor: bgColor,
      }
      this.startLoader()
      this.canvasStore.setCanvasError(false)
      const response = await creativeService.enhanceCreative(requestData)
      const imageUrl = response.imageUrl
      const updatedItems = enhancingItems.map((item: EnhancingItem) => ({
        ...item,
        remove: { ...item.remove, [key]: imageUrl },
      }))
      this.enhancementStore.setEnhancingItems(updatedItems)
      const name = (selectedItem != null && enhancingItems[selectedItem].name) || ''
      const item = enhancingItems.find((item: EnhancingItem) => item.name == name)
      if (name && item) {
        item.apply[key] = imageUrl
        this.enhancementStore.updateEnhancingItem(item)
      }
      if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
        await this.updateLogoOnShuffling(false)
      }
      await this.getGlowOfLogo(backDrop)
      await this.addBackgroundImage(this.canvasStore.fabricCanvas, imageUrl)
      if (this.editorStore.isGlowShadowApplied && isGlowShadowSupported) {
        this.createGlowShadow()
      }

      this.brandStore.selectedColor = backgroundColor
      this.canvasStore.textColor = textColor
      const layers = this.canvasStore.fabricCanvas.getObjects().reduce((acc: any, obj: any) => {
        acc[obj.layerName] = obj
        return acc
      }, {})
      const headlineObj = layers['Headline']
      if (headlineObj) {
        headlineObj.set('fill', textColor)
      }

      const subheadlineObj = layers['SubHeadline']
      if (subheadlineObj) {
        subheadlineObj.set('fill', textColor)
      }

      let layerObj = layers['CTA']
      if (layers['CTA - Order now'] && layers['CTA - Order now'].visible) {
        layerObj = layers['CTA - Order now']
      }
      const textboxObj = layerObj?._objects.find((obj: any) => obj.type === 'textbox')
      if (textboxObj) {
        let finalColor = textColor
        finalColor = this.getBackdropCTAColor(textColor, backgroundColor)
        textboxObj.set('fill', finalColor)
      }
    } catch {
      this.canvasStore.showSnackbarMessage('Failed to enhance creative!', 'error')
      throw new Error('Enhancement failed')
    } finally {
      this.stopLoader()
    }
  }

  addBackgroundImage = async (fabricCanvas: any, url: string) => {
    try {
      if (!fabricCanvas) return
      const canvasBgImageUrl = fabricCanvas?.backgroundImage?.src
      if (canvasBgImageUrl && canvasBgImageUrl === url) return
      const img = (await FabricImage.fromURL(url, { crossOrigin: 'anonymous' })) as any
      img.layerId = fabricCanvas.backgroundImage?.layerId
      img.layerName = fabricCanvas.backgroundImage?.layerName
      img.layerType = fabricCanvas.backgroundImage?.layerType
      const adSize = this.placementStore.selectedPlacement?.creativeSetGuidelines.ad_size
      const defaultSize = this.adSizeConfigStore.canvasSize
      fabricCanvas.backgroundImage = img
      img.scaleToWidth(adSize?.width || defaultSize.width)
      img.scaleToHeight(adSize?.height || defaultSize.height)
      img.selectable = true
      img.evented = false
      await fabricCanvas.requestRenderAll()
    } catch { }
  }

  getGlowOfLogo = async (item: backdrop | background | null) => {
    const logoUrl = this.brandStore.selectedLogo?.logoUrl || ''
    const logoInfo = item && item.logoInfo.find((logo) => logo.logoUrl === logoUrl)
    if (item != null) {
      await showGlowLayer(this.canvasStore.fabricCanvas, logoUrl, item.backgroundColor, item)
    } else if (logoInfo && item != null) {
      const glowObj = this.canvasStore.fabricCanvas
        ?.getObjects()
        .find((obj: any) => obj.layerName === 'Glow')
      if (glowObj) {
        glowObj.visible = logoInfo.logoRequired
        glowObj.selectable = false
        glowObj.evented = false
      }
    }
  }

  selectNewBackground = async (index: number, isTriggeredFromUndo: boolean = false) => {
    try {
      const creativeService = new CreativeService()
      this.editorStore.selectedBackground = '#background' + index
      this.editorStore.selectedBackdrop = ''
      this.editorStore.selectedBackgroundOption = 0
      this.editorStore.selectedBackGroundIndex = index
      this.editorStore.selectedBackDropIndex = null
      const backgrounds = this.editorStore.createdBackGrounds
      const aiBackgroundImages = this.placementStore.aiBackgroundImages
      const backGround = backgrounds[index]
      let textColor = JSON.parse(JSON.stringify(backGround.textColor))
      if (textColor.startsWith('rgb')) {
        this.brandStore.selectedTextColorType = 1
        const color = this.parseRgbString(textColor)
        if (color) {
          textColor = rgbToHex(color?.r, color?.g, color.b)
        }
      } else {
        this.brandStore.selectedTextColorType = 0
      }
      removeDuoToneBackground(this.canvasStore.fabricCanvas)
      this.startLoader()
      this.canvasStore.textColor = textColor
      const layers = this.canvasStore.fabricCanvas.getObjects().reduce((acc: any, obj: any) => {
        acc[obj.layerName] = obj
        return acc
      }, {})

      this.placementStore.setSelectedAIBackgroundImage(
        this.placementStore.aiBackgroundImages[backGround.backgroundImageIndex ?? 0],
      )
      this.placementStore.setSelectedBackgroundImage('')
      if (this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER) {
        this.applyGlowShadow(true, undefined, isTriggeredFromUndo)
      }
      const needChangeOfOverlayColor =
        backGround.backgroundTextureIndex &&
        this.placementStore.selectedOverlayImage != overlays[backGround.backgroundTextureIndex]

      if (backGround.backgroundTextureIndex === null) {
        this.placementStore.setOverlayImage('')
      } else {
        this.placementStore.setOverlayImage(overlays[backGround.backgroundTextureIndex])
      }
      this.placementStore.setOverlayImage(overlays[backGround.backgroundTextureIndex ?? 0])

      let backgroundColor = backGround.backgroundColor
      const bgUrl = aiBackgroundImages[backGround.backgroundImageIndex]
      if (backGround.backgroundTextureIndex == 0) {
        const htmlImage = await loadHtmlImage(bgUrl)
        const dominantColor = getDominantColor(htmlImage)
        const hexColor = rgbToHex(dominantColor.r, dominantColor.g, dominantColor.b)
        backGround.backgroundColor = hexColor
        this.brandStore.selectedColor = hexColor
        if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
          await this.updateLogoOnShuffling(false)
        }
        await this.getGlowOfLogo(backGround)
        await this.addBackgroundImage(this.canvasStore.fabricCanvas, bgUrl)
        let layerObj = layers['CTA']
        if (layers['CTA - Order now'] && layers['CTA - Order now'].visible) {
          layerObj = layers['CTA - Order now']
        }
        const textboxObj = layerObj?._objects.find((obj: any) => obj.type === 'textbox')
        if (textboxObj) {
          textboxObj.set('fill', this.BLACK)
        }
        this.canvasStore.setLayers(layers)
        this.canvasStore.fabricCanvas.renderAll()
        this.stopLoader()
        return
      }
      let layerObj = layers['CTA']
      if (layers['CTA - Order now'] && layers['CTA - Order now'].visible) {
        layerObj = layers['CTA - Order now']
      }
      const textboxObj = layerObj?._objects.find((obj: any) => obj.type === 'textbox')
      if (textboxObj) {
        textboxObj.set('fill', this.BLACK)
      }

      await this.addBackgroundImage(this.canvasStore.fabricCanvas, bgUrl)
      if (needChangeOfOverlayColor || backgroundColor == '') {
        backgroundColor = await this.getOverlayColor()
      }

      backGround.backgroundColor = backgroundColor
      this.brandStore.selectedColor = backgroundColor

      let url = ''
      this.canvasStore.setCanvasError(false)
      const selectedOverlayIndex = backGround.backgroundTextureIndex
      backGround.backgroundColor = backgroundColor
      this.canvasStore.textColor = backGround.textColor
      if (!this.editorStore.isLogoLocked && !this.editorStore.isShufflingPlacement) {
        await this.updateLogoOnShuffling(false)
      }
      await this.getGlowOfLogo(backGround)

      if (selectedOverlayIndex !== null && selectedOverlayIndex !== 0) {
        const overlayUrl = overlays[selectedOverlayIndex ?? 0]
        url = await creativeService.GenerateBackgroundWithOverlay(
          this.appStore.selectedBusinessProfileId,
          {
            brandName: this.brandStore.selectedBrand?.name || '',
            bgUrl,
            overlayUrl,
            color: backgroundColor,
          } as GenerateBgWithOverlayRequest,
        )
        await this.addBackgroundImage(this.canvasStore.fabricCanvas, url)
      }

      const headlineObj = layers['Headline']
      if (headlineObj) {
        headlineObj.set('fill', textColor)
      }

      const subheadlineObj = layers['SubHeadline']
      if (subheadlineObj) {
        subheadlineObj.set('fill', textColor)
      }
    } catch {
      this.canvasStore.showSnackbarMessage('Failed to enhance creative!', 'error')
    } finally {
      this.stopLoader()
    }
  }

  getOverlayColor = async (fabricCanvas: Canvas | null = null) => {
    const canvas = fabricCanvas || this.canvasStore.fabricCanvas
    const nonProductLayers = [
      'Headline',
      'SubHeadline',
      'CTA',
      'CTA - Order Now',
      'Logo',
      'Logo - Horizontal',
    ]
    const layersToHide = canvas
      .getObjects()
      .filter((obj: any) => nonProductLayers.includes(obj.layerName) && obj.visible)

    // Hide all layers to capture products-only image
    layersToHide.forEach((layer: FabricObject) => layer.set({ visible: false }))
    canvas.backgroundImage?.set({ visible: false })
    canvas.backgroundColor = null
    canvas.renderAll.bind(canvas)()

    canvas.renderAll()
    const imageWithProducts = canvas.toDataURL({
      format: 'png',
      multiplier: 1,
    })
    const prominentColors = await getProminentColors(imageWithProducts)
    //choose random prominent color
    const randomIndex = getRandomNumber(0, 0, prominentColors.length - 1)
    const prominentColor = prominentColors[randomIndex]
    const rgbColor = this.parseRgbString(prominentColor)
    if (!rgbColor) {
      throw new Error('Invalid RGB color format')
    }
    const hsl = rgbToHsl(rgbColor)
    // Set luminosity to 0.8 (80%)
    const adjustedRgb = hslToRgb(hsl[0], hsl[1], 0.8)
    const hexColor = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b)
    const argb = argbFromHex(hexColor)
    const palette = TonalPalette.fromInt(argb)
    const backgroundColor = hexFromArgb(palette.tone(90))

    //reset all layers to visible
    layersToHide.forEach((layer: FabricObject) => layer.set({ visible: true }))
    canvas.backgroundImage?.set({ visible: true })
    canvas.backgroundColor = '#FFFFFF'
    canvas.renderAll()
    return backgroundColor
  }
  // Advanced Settings Methods

  updateShadowBrightness = (increase: boolean) => {
    const shadows = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName?.includes('Shadow'))

    if (shadows.length) {
      shadows.forEach((shadow: any) => {
        const newOpacity = Math.max(
          0.01,
          Math.min(1, parseFloat(shadow.opacity) + (increase ? 0.1 : -0.1)),
        )
        shadow.opacity = newOpacity
        this.editorStore.setShadowOpacity(newOpacity)
      })
    } else {
      const products = this.canvasStore.fabricCanvas
        .getObjects()
        .filter((obj: any) => obj.layerName?.includes('Product'))

      if (products.length) {
        products.forEach((product: any) => {
          if (product.shadow) {
            const newColor = this.adjustRgbaOpacity(product.shadow.color, increase ? 0.1 : -0.1)
            product.shadow = new Shadow({
              ...product.shadow,
              color: newColor,
            })
          }
        })
      }
    }

    this.canvasStore.fabricCanvas.renderAll()
  }

  handleProductTilt = async (strategy: TiltStrategy, increase: boolean) => {
    if (this.editorStore.syncingShadow) return
    this.editorStore.setSelectedTiltStrategy(strategy)
    this.setOptimalAnchors(false)

    const products = this.getProductsInOrder()
    const tiltAmount = TILT_ANGLES.SMALL
    const direction = increase ? 0.2 : -0.2

    // Apply tilt based on strategy
    this.applyTiltToProducts(products, strategy, direction, tiltAmount)

    // Sync shadows after tilt
    await this.syncShadowWithNewChanges()
  }

  scaleProducts = async (increase: boolean) => {
    if (this.editorStore.syncingShadow) return

    this.editorStore.setIncreaseProductSize(true)
    this.editorStore.setDecreaseProductSize(true)
    const products = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName?.includes('Product'))
    const { group } = this.getScalingGroup()
    if (!group) return
    const scale = increase ? 0.01 : -0.01
    const initialWidth = group.getScaledWidth()
    const initialHeight = group.getScaledHeight()

    // Apply scaling
    group.scaleX += scale
    group.scaleY += scale

    this.editorStore.setGroupScaleX(this.editorStore.groupScaleX + scale)
    this.editorStore.setGroupScaleY(this.editorStore.groupScaleY + scale)

    // Adjust position to maintain origin
    this.adjustPositionAfterScaling(group, initialWidth, initialHeight)

    // Check constraints and update UI state
    this.updateScalingConstraints(group)
    const defaultArea = getDefaultPlacementArea()
    const topViolation = group.topEdge < defaultArea.top
    const bottomViolation = group.bottomEdge > defaultArea.bottom
    const leftMostProduct = group
    this.canvasStore.fabricCanvas.renderAll()
    this.ungroupObjects('productsGroup')
    const MIN_LEFT = Math.min(this.placementStore.currentPlacementArea.left, this.adSizeConfigStore.minLeftConstraint)
    if (leftMostProduct.leftEdge <= MIN_LEFT) {
      this.editorStore.setIncreaseProductSize(false)
    } else {
      this.editorStore.setIncreaseProductSize(true)
    }
    // Reflow text and sync shadows
    this.triggerTextReflow()
    if (topViolation) {
      this.editorStore.setIncreaseProductSize(false)
      await this.syncShadowWithNewChanges(products?.length == 1 ? false : true)
      this.canvasStore.fabricCanvas.renderAll()
      return
    }

    if (bottomViolation && this.placementStore.selectedPlacementOption != PLACEMENT_TYPES.CORNER) {
      this.editorStore.setIncreaseProductSize(false)
      await this.syncShadowWithNewChanges(products?.length == 1 ? false : true)
      this.canvasStore.fabricCanvas.renderAll()
      return
    }

    if (this.editorStore.groupScaleX <= 0.7 || this.editorStore.groupScaleY <= 0.7) {
      this.editorStore.setDecreaseProductSize(false)
    }
    await this.syncShadowWithNewChanges(products?.length == 1 ? false : true)

    this.canvasStore.fabricCanvas.renderAll()
  }

  updateShadowLighting = (left: boolean) => {
    const supportedDirections = this.getSupportedShadowDirections()
    let newIndex = this.editorStore.shadowLightingIndex + (left ? -1 : 1)

    if (newIndex < 0) {
      newIndex = supportedDirections.length - 1
    } else if (newIndex >= supportedDirections.length) {
      newIndex = 0
    }

    const products = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName?.includes('Product'))

    if (products.length) {
      products.forEach((product: any) => {
        product.shadow = createShadow(
          product,
          'front', // Use current placement
          supportedDirections[newIndex],
        )
      })

      this.editorStore.setShadowLightingIndex(newIndex)
      this.canvasStore.fabricCanvas.renderAll()
    }
  }

  applyGlowShadow = (removeShadow?: boolean, color?: string, isTriggeredFromUndo?: boolean) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isTriggeredFromUndo
      ? this.editorStore.setIsGlowShadowTriggeredFromUndo(true)
      : this.editorStore.setIsGlowShadowTriggeredFromUndo(false)

    if (removeShadow) {
      // Remove glow shadow
      this.createGlowShadow(removeShadow)
      this.editorStore.setIsGlowShadowApplied(false)
      this.editorStore.setGlowColor(null)
    } else {
      // Apply glow shadow
      this.createGlowShadow(removeShadow, color)
      this.editorStore.setIsGlowShadowApplied(true)
      this.editorStore.setShadowLightingIndex(0)
    }

    this.canvasStore.fabricCanvas.renderAll()
  }

  changeTiltStrategy = (strategy: TiltStrategy) => {
    this.editorStore.setSelectedTiltStrategy(strategy)
    this.resetProductAnglesToOriginal()
    this.setOptimalAnchors()
  }

  expandDropShadow = (increase: boolean) => {
    const shadows = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName == 'Shadow')

    if (shadows.length) {
      shadows.forEach((shadow: any) => {
        const initialWidth = shadow.getScaledWidth()

        const newScale = Math.max(
          0.01,
          Math.min(1, parseFloat(shadow.scaleX) + (increase ? 0.01 : -0.01)),
        )

        shadow.scaleX = newScale
        this.editorStore.setShadowScale(newScale)

        if (shadow.layerType == 'rotate') {
          const currentWidth = shadow.getScaledWidth()

          if (shadow.isLeftShadow) {
            if (increase) {
              shadow.left += (currentWidth - initialWidth) / 2
            } else {
              shadow.left -= (initialWidth - currentWidth) / 2
            }
          } else {
            if (increase) {
              shadow.left -= (currentWidth - initialWidth) / 2
            } else {
              shadow.left += (initialWidth - currentWidth) / 2
            }
          }
        }
      })

      this.canvasStore.fabricCanvas.renderAll()
    }
  }

  // Helper methods (internal, no undo/redo tracking needed)

  private adjustRgbaOpacity = (rgba: string, value: number): string => {
    const match = rgba.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+),\s*(\d*\.?\d+)\s*\)/)

    if (match) {
      const [, r, g, b, a] = match
      const newOpacity = Math.max(0.01, Math.min(1, parseFloat(a) + value))
      this.editorStore.setShadowOpacity(newOpacity)
      return `rgba(${r}, ${g}, ${b}, ${newOpacity})`
    }

    return rgba
  }

  private getProductsInOrder = () => {
    const glowGroup = this.canvasStore.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

    if (glowGroup) {
      return [glowGroup]
    }

    return this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName.includes('Product'))
      .sort((a: any, b: any) => a.left - b.left)
  }

  private applyTiltToProducts = (
    products: any[],
    strategy: TiltStrategy,
    direction: number,
    tiltAmount: number,
  ) => {
    switch (strategy.value) {
      case TILT_STRATEGIES.SINGLE_PRODUCT.LEFT:
        // Apply tilt to single product
        if (products[0]) {
          const currentAngle = products[0].angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          products[0].set('angle', newAngle)
          products[0].setCoords()
        }
        break

      case TILT_STRATEGIES.DUAL_PRODUCT.BOTH_SAME:
        // Both products tilt in same direction
        products.forEach((product) => {
          const currentAngle = product.angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          product.set('angle', newAngle)
          product.setCoords()
        })
        break

      case TILT_STRATEGIES.DUAL_PRODUCT.LEFT_ONLY:
        // Only left product tilts
        if (products[0]) {
          const currentAngle = products[0].angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          products[0].set('angle', newAngle)
          products[0].setCoords()
        }
        break

      case TILT_STRATEGIES.DUAL_PRODUCT.RIGHT_ONLY:
        // Only right product tilts
        if (products[1]) {
          const currentAngle = products[1].angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          products[1].set('angle', newAngle)
          products[1].setCoords()
        }
        break

      case TILT_STRATEGIES.DUAL_PRODUCT.OPPOSITE:
        // Products tilt in opposite directions
        if (products[0]) {
          const currentAngle = products[0].angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          products[0].set('angle', newAngle)
          products[0].setCoords()
        }
        if (products[1]) {
          const currentAngle = products[1].angle || 0
          const newAngle = currentAngle - tiltAmount * direction // Opposite direction
          products[1].set('angle', newAngle)
          products[1].setCoords()
        }
        break

      case TILT_STRATEGIES.TRIPLE_PRODUCT.ALL_SAME:
        // All three products tilt same direction
        products.forEach((product) => {
          const currentAngle = product.angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          product.set('angle', newAngle)
          product.setCoords()
        })
        break

      case TILT_STRATEGIES.TRIPLE_PRODUCT.FAN_LEFT:
        // Fan pattern: left most tilt, middle less, right unchanged
        products.forEach((product, index) => {
          const currentAngle = product.angle || 0
          let multiplier = 0
          switch (index) {
            case 0:
              multiplier = 1
              break // Left: full tilt
            case 1:
              multiplier = 0.5
              break // Middle: half tilt
            case 2:
              multiplier = 0
              break // Right: no tilt
          }
          const newAngle = currentAngle + tiltAmount * direction * multiplier
          product.set('angle', newAngle)
          product.setCoords()
        })
        break

      case TILT_STRATEGIES.TRIPLE_PRODUCT.SIDE_TILT:
        // Side products tilt opposite, middle unchanged
        products.forEach((product, index) => {
          const currentAngle = product.angle || 0
          let newAngle = currentAngle
          switch (index) {
            case 0: // Left tilts one way
              newAngle = currentAngle + tiltAmount * direction
              break
            case 1: // Middle unchanged
              break
            case 2: // Right tilts opposite way
              newAngle = currentAngle - tiltAmount * direction
              break
          }
          product.set('angle', newAngle)
          product.setCoords()
        })
        break

      default:
        // Fallback: apply to all products
        products.forEach((product) => {
          const currentAngle = product.angle || 0
          const newAngle = currentAngle + tiltAmount * direction
          product.set('angle', newAngle)
          product.setCoords()
        })
        break
    }

    this.canvasStore.fabricCanvas.requestRenderAll()
  }

  private getScalingGroup = () => {
    const glowGroup = this.canvasStore.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

    if (glowGroup) {
      return { group: glowGroup, products: null }
    }
    const products = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName?.includes('Product'))
    if (products.length === 1) {
      return { group: products[0] }
    }
    const newGroup = this.createGroup(this.canvasStore.fabricCanvas, products)
    newGroup.set('layerName', 'productsGroup')
    this.canvasStore.fabricCanvas.add(newGroup)
    this.canvasStore.fabricCanvas.bringObjectToFront(newGroup)
    this.canvasStore.fabricCanvas.requestRenderAll()

    return { group: newGroup }
  }

  createGroup = (canvas: Canvas, images: any[]) => {
    const boundingRect = images.reduce(
      (acc, img) => {
        const { left, top } = img.getBoundingRect()

        return {
          left: Math.min(acc.left, left),
          top: Math.min(acc.top, top),
          right: Math.max(acc.right, left + img.getScaledWidth()),
          bottom: Math.max(acc.bottom, top + img.getScaledHeight()),
        }
      },
      {
        left: Infinity,
        top: Infinity,
        right: -Infinity,
        bottom: -Infinity,
      },
    )

    // Create group and add accurately positioned clones
    const group = new Group([], {
      left: boundingRect.left,
      top: boundingRect.top,
      originX: 'left',
      originY: 'top',
      subTargetCheck: true,
      interactive: false,
    })

    images.forEach((img) => {
      const absolutePoint = img.getPointByOrigin(img.originX, img.originY)

      const relativeLeft = absolutePoint.x - boundingRect.left
      const relativeTop = absolutePoint.y - boundingRect.top

      const cloned = new FabricImage(img.getElement(), {
        left: relativeLeft,
        top: relativeTop,
        scaleX: img.scaleX,
        scaleY: img.scaleY,
        angle: img.angle,
        originX: img.originX,
        originY: img.originY,
        layerId: img.layerId,
        layerName: img.layerName,
        originalAngle: img.originalAngle,
        objectCaching: false,
      })

      if (!this.editorStore.isGlowShadowApplied) {
        cloned.shadow = img.shadow
      }

      canvas.remove(img)
      group.add(cloned)
    })
    group.set({
      left: boundingRect.left,
      top: boundingRect.top,
    })
    group.setCoords()

    return group
  }
  private adjustPositionAfterScaling = (
    group: any,
    initialWidth: number,
    initialHeight: number,
  ) => {
    const currentWidth = group.getScaledWidth()
    const currentHeight = group.getScaledHeight()
    const widthDiff = currentWidth - initialWidth
    const heightDiff = currentHeight - initialHeight

    // Adjust position based on origin (simplified)
    switch (group.originX) {
      case 'left':
        group.left -= widthDiff
      case 'center':
        group.left -= widthDiff / 2
        break
      case 'right':
        break
    }

    if (
      this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.CORNER ||
      this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.FRONT ||
      group.originY != 'center'
    ) {
      switch (group.originY) {
        case 'top':
          group.top -= heightDiff
          break
        case 'center':
          group.top -= heightDiff / 2
          break
        case 'bottom':
          group.top += heightDiff / 2
      }
    }

    group.setCoords()
  }

  private updateScalingConstraints = (group: any) => {
    const boundingBox = group.getBoundingRect()
    group.topEdge = boundingBox.top
    group.bottomEdge = boundingBox.top + boundingBox.height
    group.leftEdge = boundingBox.left
    group.rightEdge = boundingBox.left + boundingBox.width
  }

  private syncShadowWithNewChanges = async (addShadowEvents = false) => {
    this.editorStore.setSyncingShadow(true)

    if (
      this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.FRONT ||
      this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.LAYER ||
      this.placementStore.selectedPlacementOption == PLACEMENT_TYPES.STAIRCASE
    ) {
      const objects = this.canvasStore.fabricCanvas.getObjects()

      for (const obj of objects) {
        if (obj.layerName?.toLowerCase().includes('product')) {
          const productId = obj.layerId
          if (productId) {
            const shadow = this.canvasStore.fabricCanvas
              .getObjects()
              .find((o: any) => (o.layerName === 'Shadow' || o.layerName == layerNames.AIShadow) && o.layerId === productId)
            if (shadow) {
              shadow.set({
                selectable: false,
                evented: false,
              })

              if (addShadowEvents) {
                syncContactShadow(obj, shadow, this.canvasStore.fabricCanvas)
              }

              // AI Shadows
              if (shadow.layerName === layerNames.AIShadow && isFlatBase(obj.angle)) {
                syncAIShadow(obj, shadow, this.canvasStore.fabricCanvas, shadow.aiShadowProperties)
              } else if (shadow.layerName !== layerNames.AIShadow && isFlatBase(obj.angle)) {
                try {
                  await generateAIShadows(this.canvasStore.fabricCanvas, [obj])
                } catch {
                  await syncShadow(obj, shadow, this.canvasStore.fabricCanvas)
                }
              } else {
                await syncShadow(obj, shadow, this.canvasStore.fabricCanvas)
              }
            }
          }
        }
      }
    }
    this.editorStore.setSyncingShadow(false)
  }

  private getSupportedShadowDirections = (): string[] => {
    if (this.editorStore.isGlowShadowApplied) {
      return []
    }

    // Simplified - would use placement store in real implementation
    return [DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
  }

  private removeGlowShadow = () => {
    const products = this.canvasStore.fabricCanvas
      .getObjects()
      .filter((obj: any) => obj.layerName?.includes('Product'))

    products.forEach((product: any) => {
      product.shadow = createShadow(product, 'front', DIRECTIONS.FRONT)
    })
  }

  private createGlowShadow = (removeShadow?: boolean, color?: string) => {
    if (removeShadow) {
      const glowGroup = this.canvasStore.fabricCanvas
        .getObjects()
        .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

      if (glowGroup) {
        this.ungroupObjects('glowGroup')
      }

      const products = this.canvasStore.fabricCanvas
        .getObjects()
        .filter((obj: any) => obj.layerName?.includes('Product'))

      if (products.length) {
        products.forEach((product: any) => {
          product.shadow = createShadow(
            product,
            this.placementStore.selectedPlacementOption,
            DIRECTIONS.FRONT,
          )
        })

        this.editorStore.setGlowColor(null)
      }

      this.canvasStore.fabricCanvas.renderAll()
      this.editorStore.setIsGlowShadowApplied(false)
    } else {
      this.editorStore.setIsGlowShadowApplied(true)
      this.editorStore.setShadowLightingIndex(0)
      let shadowColor = undefined
      const glowColor = this.editorStore.glowColor
      if (glowColor !== AMBIENT_SHADOW_COLOR && glowColor !== DROP_SHADOW_COLOR) {
        shadowColor = glowColor
      }
      const glowGroup = this.canvasStore.fabricCanvas
        .getObjects()
        .find((obj: any) => obj.layerName == 'glowGroup' && obj.type == 'group')

      if (glowGroup) {
        glowGroup.shadow = createGlowShadow(glowGroup, color)
        if (glowGroup.shadow.color) {
          this.editorStore.setGlowColor(glowGroup.shadow.color)
        }
      } else {
        const products = this.canvasStore.fabricCanvas
          .getObjects()
          .filter((obj: any) => obj.layerName?.includes('Product'))

        if (products.length == 1) {
          products.forEach((product: any) => {
            product.shadow = createGlowShadow(product, color)
            if (product.shadow.color) {
              this.editorStore.setGlowColor(product.shadow.color)
            }
          })
        } else if (products.length > 1) {
          this.createGroupedGlowShadow(this.canvasStore.fabricCanvas, products, color)
        }
      }

      this.canvasStore.fabricCanvas.renderAll()
    }
  }

  private ungroupObjects = (layerName: string) => {
    const productGroup = this.canvasStore.fabricCanvas
      .getObjects()
      .find((obj: any) => obj.layerName == layerName && obj.type == 'group')

    if (productGroup) {
      this.canvasStore.fabricCanvas.add(...productGroup.removeAll())
      this.canvasStore.fabricCanvas.remove(productGroup)
      this.canvasStore.fabricCanvas.renderAll()
    }
  }

  private createGroupedGlowShadow = (canvas: Canvas, images: any[], color?: string) => {
    const group: any = this.createGroup(canvas, images)
    const glowShadow = createGlowShadow(group, color)
    group.set('shadow', glowShadow)
    if (glowShadow.color) {
      this.editorStore.setGlowColor(glowShadow.color)
    }
    group.set('layerName', 'glowGroup')
    canvas.add(group)
    canvas.bringObjectToFront(group)
    canvas.requestRenderAll()
  }

  private resetProductAnglesToOriginal = () => {
    const products = this.getProductsInOrder()

    products.forEach((product: any) => {
      product.set('angle', product.originalAngle || 0)
      product.setCoords()
    })

    this.syncShadowWithNewChanges()
    this.canvasStore.fabricCanvas.requestRenderAll()
  }

  private setOptimalAnchors = (shouldRenderAll: boolean = true) => {
    const products = this.getProductsInOrder()

    products.forEach((product: any) => {
      const { originX, originY } = this.getOptimalOrigin()

      // Only adjust if origins are different
      if (product.originX !== originX || product.originY !== originY) {
        this.adjustPositionForNewOrigin(product, originX, originY)
      }
    })

    if (shouldRenderAll) {
      this.canvasStore.fabricCanvas.renderAll()
    }
  }

  private changeTexturesAccordingToPlacement = (
    previousPlacement: string,
    newPlacement: string,
    preserveTexture: boolean = false,
  ) => {
    if (
      this.specialPlacements.includes(previousPlacement) &&
      !this.specialPlacements.includes(newPlacement)
    ) {
      for (let i = 0; i < this.editorStore.createdBackDrops.length; i++) {
        const backdrop = this.editorStore.createdBackDrops[i]
        const specialIndex = this.editorStore.specialIndex.find((item) => item.index === i)
        if (specialIndex && !(i === this.editorStore.selectedBackDropIndex && preserveTexture)) {
          backdrop.backgroundTextureIndex = specialIndex.normalIndex
        }
      }
    } else if (
      this.specialPlacements.includes(newPlacement) &&
      !this.specialPlacements.includes(previousPlacement)
    ) {
      for (let i = 0; i < this.editorStore.createdBackDrops.length; i++) {
        const specialIndex = this.editorStore.specialIndex.find((item) => item.index === i)
        if (specialIndex && !(i === this.editorStore.selectedBackDropIndex && preserveTexture)) {
          this.editorStore.createdBackDrops[i].backgroundTextureIndex = specialIndex.specialIndex
        }
      }
    }
  }

  updateLogo = async (logo: Logo, showLoader: boolean = true) => {
    const logoUrl = logo.logoUrl
    if (logoUrl) {
      if (showLoader) this.startLoader()
      let isRectangleLogo = false
      try {
        const aspectRatio = await getImageAspectRatio(logoUrl)
        if (aspectRatio >= squareAspectRatioBuffer) {
          isRectangleLogo = true
        }
      } catch (e) {
        console.error('Error calculating the aspect ratio of logo:', e)
      }
      this.canvasStore.setLogoUrl(logoUrl)
      this.brandStore.setSelectedLogo(logo)
      const fabricCanvas = this.canvasStore.fabricCanvas
      const defaultJson = this.placementStore.selectedPublishedVariant?.canvasJson
      const defaultLogoObj = isRectangleLogo
        ? defaultJson?.objects.find((obj: any) => obj.layerName === 'Logo - Horizontal')
        : defaultJson?.objects.find((obj: any) => obj.layerName === 'Logo')

      if (!defaultLogoObj) {
        console.error('No default logo object found in JSON.')
        return
      }

      // Find and remove any existing logo objects
      if (
        this.editorStore.selectedBackgroundOption === 0 &&
        this.editorStore.selectedBackGroundIndex != null
      ) {
        const background =
          this.editorStore.createdBackGrounds[this.editorStore.selectedBackGroundIndex]
        await showGlowLayer(
          fabricCanvas,
          this.brandStore.selectedLogo?.logoUrl,
          this.brandStore.selectedColor,
          background,
        )
      } else if (this.editorStore.selectedBackDropIndex != null) {
        const background = this.editorStore.createdBackDrops[this.editorStore.selectedBackDropIndex]
        await showGlowLayer(
          fabricCanvas,
          this.brandStore.selectedLogo?.logoUrl,
          this.brandStore.selectedColor,
          background,
        )
      }
      const logoObjs = fabricCanvas
        ?.getObjects()
        .filter((obj: any) => obj.layerName === (isRectangleLogo ? 'Logo - Horizontal' : 'Logo'))
      logoObjs?.forEach((obj: any) => fabricCanvas?.remove(obj))
      const squareLogoObj = fabricCanvas?.getObjects().find((obj: any) => obj.layerName === 'Logo')
      const rectangleLogoObj = fabricCanvas
        ?.getObjects()
        .find((obj: any) => obj.layerName === 'Logo - Horizontal')
      const canvasJson = this.canvasStore.canvasJson
      const canvasJsonSquareLogoObj = canvasJson?.objects.find(
        (obj: any) => obj.layerName === 'Logo',
      )
      const canvasJsonRectangleLogoObj = canvasJson?.objects.find(
        (obj: any) => obj.layerName === 'Logo - Horizontal',
      )
      if (isRectangleLogo) {
        canvasJsonRectangleLogoObj.visible = true
        canvasJsonSquareLogoObj.visible = false
        squareLogoObj.set({ visible: false })
      } else {
        canvasJsonSquareLogoObj.visible = true
        canvasJsonRectangleLogoObj.visible = false
        rectangleLogoObj.set({ visible: false })
      }

      const originalPosition = {
        left: defaultLogoObj.left,
        top: defaultLogoObj.top,
        originX: defaultLogoObj.originX,
        originY: defaultLogoObj.originY,
      }

      if (logoUrl.toLowerCase().endsWith('.svg')) {
        try {
          const resultSvg = await loadSVGFromURL(logoUrl)
          const logoGroup = util.groupSVGElements(
            resultSvg.objects as FabricObject[],
            resultSvg.options,
          ) as Group

          const groupWidth = logoGroup.width || logoGroup.getBoundingRect().width
          const groupHeight = logoGroup.height || logoGroup.getBoundingRect().height

          const scaleX = defaultLogoObj.width / groupWidth
          const scaleY = defaultLogoObj.height / groupHeight
          const scale = Math.min(scaleX, scaleY)

          logoGroup.set({
            scaleX: scale,
            scaleY: scale,
            ...originalPosition,
            layerName: 'Logo',
          })
          logoGroup.setCoords()
          fabricCanvas?.add(logoGroup)
          this.canvasStore.setFabricCanvas(fabricCanvas)
          this.canvasStore.fabricCanvas?.renderAll()
        } catch (error) {
          console.error('Error loading SVG:', error)
        } finally {
          if (showLoader) this.stopLoader()
        }
      } else {
        try {
          const img = await FabricImage.fromURL(logoUrl, { crossOrigin: 'anonymous' })

          const defaultWidth = defaultLogoObj.width * defaultLogoObj.scaleX
          const defaultHeight = defaultLogoObj.height * defaultLogoObj.scaleY

          const scaleX = defaultWidth / img.width!
          const scaleY = defaultHeight / img.height!
          const scale = Math.min(scaleX, scaleY)

          img.set({
            scaleX: scale,
            scaleY: scale,
            ...originalPosition,
            layerName: isRectangleLogo ? 'Logo - Horizontal' : 'Logo',
            selectable: false,
            evented: false,
          })
          img.setCoords()
          fabricCanvas?.add(img)
          this.canvasStore.setFabricCanvas(fabricCanvas)
          this.canvasStore.setCanvasJson(canvasJson)
          this.canvasStore.fabricCanvas?.renderAll()
        } catch (error) {
          console.error('Error loading image:', error)
        } finally {
          if (showLoader) this.stopLoader()
        }
      }
    }
  }

  handlePlacementChange = async (
    placement: string,
    angle: number = 0,
    lastPlacement: string = '',
    isShufflingPlacement: boolean = false,
    selectedBackgroundType?: number, // special for undo redo
    selectedBackgroundIndex?: number, // special for undo redo
  ) => {
    if (lastPlacement === '') {
      lastPlacement = this.placementStore.selectedPlacementOption
    }
    if (lastPlacement === placement && !isShufflingPlacement) {
      return
    }
    const brandColorsLength =
      this.brandStore.selectedBrand?.colours.length ?? this.brandStore.colours.length - 4
    this.startLoader()
    const currentBackDrops = this.editorStore.createdBackDrops
    this.placementStore.selectedPlacementOption = placement
    const productDataItems = this.editorStore.activeProductDataItems

    if (
      this.placementStore.processedProductData.length > 0 &&
      this.placementStore.defaultPlacement
    ) {
      this.placementStore.defaultPlacement.placement = placement
      this.placementStore.defaultPlacement.angle = angle
      const supportedLightDirections = getLightDirectionsForPlacementType(placement)
      // Disable glow (set isGlowShadowApplied=false & glowColor=null) for non-FLOAT/CORNER placements to prevent UI shadow issues
      if (!(placement === PLACEMENT_TYPES.FLOAT || placement === PLACEMENT_TYPES.CORNER)) {
        this.editorStore.setIsGlowShadowApplied(false)
        this.editorStore.setGlowColor(null)
      }

      const previousPlacement =
        lastPlacement !== '' ? lastPlacement : this.placementStore.selectedPlacementOption
      this.placementStore.selectedPlacementOption = placement

      // This is done to handle undo redo for background and backdrop.
      // If selectedBackgroundType and selectedBackgroundIndex are provided, we need to select the background or backdrop at the selected index.
      if (selectedBackgroundType != undefined && selectedBackgroundIndex != undefined) {
        this.changeTexturesAccordingToPlacement(previousPlacement, placement)
        if (selectedBackgroundType == 0) {
          await this.selectNewBackground(selectedBackgroundIndex)
        } else {
          await this.selectNewBackDrop(selectedBackgroundIndex)
        }

        this.startLoader()
        await loadPlacements(
          this.canvasStore.fabricCanvas,
          productDataItems.map((p) => {
            const product: any = p.fabricImage

            const selectedProduct =
              this.productStore.selectedProducts.find((sp) => sp.productUrl == p.productData.url) ||
              this.editorStore.activeProducts.find((ap) => ap.productUrl == p.productData.url)
            if (selectedProduct) {
              product.productId = selectedProduct.id
              if ((selectedProduct as any).productInfo?.productData) {
                product.isMultiple = (selectedProduct as any).productInfo.productData.isMultiple
              }
            }
            return product
          }) as FabricImage[],
          placement,
          this.placementStore.defaultPlacement.mainShadow,
          this.placementStore.defaultPlacement.lightDirection || supportedLightDirections[0],
          angle,
          (this.placementStore.defaultPlacement as any).aspectCounts,
        )
        this.canvasStore.setCanvasJson(
          this.getCanvasJsonWithCustomInfo(this.canvasStore.fabricCanvas),
        )

        await this.syncShadowWithNewChanges(true)
        this.canvasStore.fabricCanvas.renderAll()
        this.canvasStore.setCanvasLoader(false)
        this.stopLoader()

        return
      }

      if (isShufflingPlacement) {
        const previousIndex =
          currentBackDrops[this.editorStore.selectedBackDropIndex ?? 0].backgroundTextureIndex
        this.changeTexturesAccordingToPlacement(previousPlacement, placement)
        const selectedIndex =
          currentBackDrops[this.editorStore.selectedBackDropIndex ?? 0].backgroundTextureIndex
        if (!this.editorStore.isBackgroundLocked) {
          const filteredBackdrops = this.editorStore.createdBackDrops.slice(
            0,
            this.editorStore.createdBackDrops.length - brandColorsLength,
          )
          const firstBackgroundIndex = filteredBackdrops.findIndex(
            (backdrop: any) => backdrop.backgroundTextureIndex === 1,
          )
          const secondBackgroundIndex = filteredBackdrops.findIndex(
            (backdrop: any) => backdrop.backgroundTextureIndex === 2,
          )
          if (
            this.specialPlacements.includes(previousPlacement) &&
            !this.specialPlacements.includes(placement) &&
            (this.specialTexturesIndex.includes(selectedIndex) ||
              this.editorStore.selectedBackGroundIndex != null)
          ) {
            const index = Math.floor(
              Math.random() * (this.editorStore.createdBackDrops.length - brandColorsLength),
            )
            await this.selectNewBackDrop(index)
          } else if (
            this.editorStore.selectedBackgroundOption === 1 &&
            !this.specialPlacements.includes(previousPlacement) &&
            this.specialPlacements.includes(placement)
          ) {
            // Weighted random selection: higher probability for first 2 backgrounds
            const random = Math.random()
            let randomIndex
            if (random < 0.2 && firstBackgroundIndex != -1) {
              // 30% chance for first background
              randomIndex = firstBackgroundIndex
            } else if (random < 0.4 && secondBackgroundIndex != -1) {
              // 30% chance for second background
              randomIndex = secondBackgroundIndex
            } else {
              // 40% chance distributed among remaining backgrounds
              randomIndex = Math.floor(
                Math.random() * (this.editorStore.createdBackDrops.length - brandColorsLength),
              )
            }

            await this.selectNewBackDrop(randomIndex)
          } else {
            if (this.editorStore.selectedBackgroundOption === 0) {
              const randomIndex = Math.floor(
                Math.random() * this.editorStore.createdBackGrounds.length,
              )
              await this.selectNewBackground(randomIndex)
            } else {
              const randomIndex = Math.floor(
                Math.random() * (this.editorStore.createdBackDrops.length - brandColorsLength),
              )
              await this.selectNewBackDrop(randomIndex)
            }
          }
        } else if (
          this.specialPlacements.includes(previousPlacement) &&
          !this.specialPlacements.includes(placement) &&
          (this.specialTexturesIndex.includes(previousIndex) ||
            this.editorStore.selectedBackGroundIndex != null)
        ) {
          if (this.specialTexturesIndex.includes(selectedIndex)) {
            const filteredBackdrops = this.editorStore.createdBackDrops.slice(
              0,
              this.editorStore.createdBackDrops.length - brandColorsLength,
            )
            const firstBackgroundIndex = filteredBackdrops.findIndex(
              (backdrop: any) => backdrop.backgroundTextureIndex > 2,
            )
            await this.selectNewBackDrop(firstBackgroundIndex)
          } else {
            await this.selectNewBackDrop(this.editorStore.selectedBackDropIndex ?? 0)
          }
        }
      } else {
        const currentBackground = currentBackDrops[this.editorStore.selectedBackDropIndex ?? 0]
        const currentBackgroundIndex = currentBackground.backgroundTextureIndex
        const currentBackgroundColor = currentBackground.backgroundColor
        const isNowNonSpecialPlacement = !this.specialPlacements.includes(placement)
        // Case A: If AI background is selected and target placement is non-special,
        // switch to a backdrop while preserving the current brand color.
        if (this.placementStore.selectedAIBackgroundImage != '' && isNowNonSpecialPlacement) {
          this.changeTexturesAccordingToPlacement(previousPlacement, placement)
          const newIndex = currentBackDrops.findIndex(
            (bd: any) => !this.specialTexturesIndex.includes(bd.backgroundTextureIndex),
          )
          await this.selectNewBackDrop(newIndex)
        } else {
          // Case B: If switching from special placement while using a special texture,
          // and the new placement is non-special, keep color but swap to non-special texture.
          const wasSpecialPlacement = this.specialPlacements.includes(previousPlacement)
          const isUsingSpecialTexture = this.specialTexturesIndex.includes(currentBackgroundIndex)

          if (wasSpecialPlacement && isNowNonSpecialPlacement && isUsingSpecialTexture) {
            const sameColorNonSpecialIndex = currentBackDrops.findIndex(
              (bd: any) =>
                bd.backgroundColor === currentBackgroundColor &&
                !this.specialTexturesIndex.includes(bd.backgroundTextureIndex),
            )
            if (sameColorNonSpecialIndex !== -1) {
              await this.selectNewBackDrop(sameColorNonSpecialIndex)
            } else {
              const chosenTextureIndex = 3
              await this.addBackDrops(
                {
                  backgroundColor: currentBackgroundColor,
                  textColor: currentBackground.textColor,
                  backgroundTextureIndex: chosenTextureIndex,
                  logoInfo: [],
                },
                'insert',
              )
            }
          }
          this.changeTexturesAccordingToPlacement(previousPlacement, placement, true)
        }
      }
      await loadPlacements(
        this.canvasStore.fabricCanvas,
        productDataItems.map((p) => {
          const product: any = p.fabricImage

          const selectedProduct = this.productStore.selectedProducts.find(sp => sp.productUrl == p.productData.url)
          if (selectedProduct) {
            product.productId = selectedProduct.id
            if ((selectedProduct as any).productInfo?.productData) {
              product.isMultiple = (selectedProduct as any).productInfo.productData.isMultiple
            }
          }
          return product
        }) as FabricImage[],
        placement,
        this.placementStore.defaultPlacement.mainShadow,
        this.placementStore.defaultPlacement.lightDirection || supportedLightDirections[0],
        angle,
        (this.placementStore.defaultPlacement as any).aspectCounts,
      )
      this.canvasStore.setCanvasJson(
        this.getCanvasJsonWithCustomInfo(this.canvasStore.fabricCanvas),
      )
    }
    if (isShufflingPlacement && !this.editorStore.isLogoLocked) {
      await this.updateLogoOnShuffling(false)
    }
    this.canvasStore.fabricCanvas.getObjects().forEach((obj: any) => {
      if (obj.layerName?.toLowerCase().includes('product')) {
        obj.controls.moveToFrontControl = new Control({
          x: -0.5,
          y: -0.5,
          offsetY: -16,
          cursorStyle: 'pointer',
          mouseUpHandler: moveToFront,
          render: renderChevronIcon('up'),
        })
        obj.controls.moveToBottomControl = new Control({
          x: -0.5,
          y: -0.5,
          offsetY: -16,
          offsetX: 22,
          cursorStyle: 'pointer',
          mouseUpHandler: moveToBottom,
          render: renderChevronIcon('down'),
        })
      }
    })
    await this.syncShadowWithNewChanges(true)
    this.canvasStore.fabricCanvas.renderAll()
    this.stopLoader()
  }

  // Updates the logo during placement shuffling if multiple logos are available.
  updateLogoOnShuffling = async (showLoader: boolean = true) => {
    const logos = this.brandStore.selectedBrand?.logoUrls.map((logo) => logo.logoUrl) || []
    if (!logos) return
    if (logos.length > 1) {
      let logoUrl = this.brandStore.selectedLogo?.logoUrl
      const highContrastLogo = await getBestContrastingLogo(this.brandStore.selectedColor, logos)
      if (highContrastLogo && highContrastLogo != logoUrl) {
        logoUrl = highContrastLogo
        const newIndex = this.brandStore.logos.findIndex((logo: Logo) => logo.logoUrl === logoUrl)
        const newLogo = this.brandStore.logos[newIndex]
        await this.updateLogo(newLogo, showLoader)
      }
    }
  }

  // Function to Get the list of available placement options.
  limitPlacements() {
    // Start with all placement options
    let availablePlacements = this.placementStore.placementOptions
    // Restrict placements if:
    // 1. Background is locked
    // 2. Selected background option is dual tone (value === 1)
    // 3. Selected background image is in the special backgrounds list
    if (this.editorStore.isBackgroundLocked && this.placementStore.selectedBackgroundImage) {
      if (
        specialBackgrounds.includes(this.placementStore.selectedBackgroundImage) ||
        this.editorStore.selectedBackgroundOption == 0
      ) {
        // Only allow FRONT, LAYER, and STAIRCASE placements
        availablePlacements = availablePlacements.filter(
          (p) =>
            p === PLACEMENT_TYPES.FRONT ||
            p === PLACEMENT_TYPES.LAYER ||
            p === PLACEMENT_TYPES.STAIRCASE,
        )
      }
    }
    const products = this.editorStore.activeProductDataItems
    const allHorizontal =
      products.length >= 2 &&
      products.every((p) => p.productData?.aspectType === PRODUCT_ASPECT_TYPES.HORIZONTAL)
    // Restrict shuffling to Front/Layer/Staircase placements when AI background image option is selected
    // If all products are horizontal → also allow CORNER placement in this case
    if (
      this.placementStore.selectedAIBackgroundImage ||
      this.editorStore.selectedBackgroundOption == 0
    ) {
      // User is starting from a special placement → only allow special placements
      availablePlacements = availablePlacements.filter(
        (option) =>
          SPECIAL_PLACEMENTS.includes(option) ||
          (allHorizontal && option === PLACEMENT_TYPES.CORNER),
      )
    }
    return availablePlacements
  }

  changeRandomShadow = async (chooseRandom: boolean = true) => {
    if (!this.placementStore.defaultPlacement) return
    const previousPlacement = this.placementStore.selectedPlacementOption
    if (
      !(
        this.editorStore.isShufflingPlacement &&
        this.editorStore.isPlacementLocked &&
        !(
          this.placementStore.selectedBackgroundImage &&
          !specialBackgrounds.includes(this.placementStore.selectedBackgroundImage) &&
          this.editorStore.selectedBackgroundOption == 0
        ) &&
        this.editorStore.enableAdvancedSettings
      )
    ) {
      if (chooseRandom) {
        const availablePlacements = this.limitPlacements()
        const randomIndex = Math.floor(Math.random() * availablePlacements.length)
        this.placementStore.selectedPlacementOption = availablePlacements[randomIndex]
      }
      const direction = getLightDirectionsForPlacementType(
        this.placementStore.selectedPlacementOption,
      )
      const availableDirections = direction.filter(
        (dir) => dir !== this.placementStore.defaultPlacement?.lightDirection && dir !== 'none',
      )
      const randomDirection =
        availableDirections[Math.floor(Math.random() * availableDirections.length)]
      this.placementStore.defaultPlacement.lightDirection = randomDirection
    }
    await this.handlePlacementChange(
      this.placementStore.selectedPlacementOption,
      getRandomNumber(0, -5, 5),
      previousPlacement,
      this.editorStore.isShufflingPlacement,
    )
    const headlinePrompt =
      this.canvasStore.discountType === 'None'
        ? 'Write a catchy headline which describes the brand, products very well'
        : 'Write a catchy headline which describes the brand, products along with discount information very well'
    const subHeadlinePrompt =
      this.canvasStore.discountType === 'None'
        ? 'A supporting line that adds more detail about the brand, products very well'
        : 'A supporting line that adds more detail about the brand, products along with discount information very well'

    if (this.editorStore.isShufflingPlacement && !this.editorStore.isHeadlineLocked) {
      if (this.canvasStore.aiGeneratedHeadlines.length === 0) {
        await this.writeUsingAI(headlinePrompt, TEXT_TYPE_NAMES.HEADLINE)
      }
      const randomHeadlineIndex = Math.floor(
        Math.random() * this.canvasStore.aiGeneratedHeadlines.length,
      )
      this.updateCanvasJsonText(
        this.canvasStore.aiGeneratedHeadlines[randomHeadlineIndex],
        TEXT_TYPE_NAMES.HEADLINE,
      )
    }

    if (this.editorStore.isShufflingPlacement && !this.editorStore.isSubheadlineLocked) {
      if (this.canvasStore.aiGeneratedSubHeadlines.length === 0) {
        await this.writeUsingAI(subHeadlinePrompt, TEXT_TYPE_NAMES.SUBHEADLINE)
      }
      const randomSubHeadlineIndex = Math.floor(
        Math.random() * this.canvasStore.aiGeneratedSubHeadlines.length,
      )
      this.updateCanvasJsonText(
        this.canvasStore.aiGeneratedSubHeadlines[randomSubHeadlineIndex],
        TEXT_TYPE_NAMES.SUBHEADLINE,
      )
    }
  }

  async generateDiscountText(textTypeName: TextTypeName) {
    // Decide prompt based on discount
    const prompt =
      this.canvasStore.discountType === discountTypes.none
        ? `Write a catchy ${textTypeName.toLowerCase()} which describes the brand, products very well`
        : `Write a catchy ${textTypeName.toLowerCase()} which describes the brand, products and discount information very well`

    await this.writeUsingAI(prompt, textTypeName)
    let newText =
      textTypeName === TEXT_TYPE_NAMES.HEADLINE
        ? this.canvasStore.aiGeneratedHeadlines[0]
        : this.canvasStore.aiGeneratedSubHeadlines[0]
    this.canvasStore.aiGeneratedSubHeadlines = []
    this.canvasStore.aiGeneratedHeadlines = []

    // Convert US → UK English if needed
    if (newText && USkeywords.some((word) => newText.toLowerCase().includes(word))) {
      newText = handleUSToUKEnglish(newText)
    }

    // Assign to editor store depending on type
    if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
      this.editorStore.setHeadline(newText)
      this.editorStore.setHeadlinePromptMenu(false)
    } else if (textTypeName === TEXT_TYPE_NAMES.SUBHEADLINE) {
      this.editorStore.setSubheadline(newText)
      this.editorStore.setSubheadlinePromptMenu(false)
    }
  }

  writeUsingAI = async (prompt: string, textTypeName: TextTypeName) => {
    const { minChars, maxChars } = this.getCharLimits(textTypeName)
    try {
      if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
        this.editorStore.setWritingHeadlineUsingAI(true)
      } else {
        this.editorStore.setWritingSubheadlineUsingAI(true)
      }
      if (
        this.appStore.selectedBusinessProfileId &&
        this.brandStore.selectedBrand &&
        prompt?.trim()
      ) {
        const response = await this.creativeService.writeUsingAI(
          this.appStore.selectedBusinessProfileId,
          {
            prompt,
            headlineType: textTypeName,
            brandName: this.brandStore.selectedBrand?.name,
            productUrls: this.editorStore.isAutoFix
              ? [this.placementStore.selectedBackgroundImage]
              : this.editorStore.activeProducts.map((product) => product.productUrl),
            minimumCharacters: minChars,
            maximumCharacters: maxChars,
            discountInfo: JSON.stringify({
              isEnabled: this.isDiscountEnabled,
              type: this.canvasStore.discountType,
              variation: this.canvasStore.discountVariation,
              value: this.canvasStore.discountValue,
              couponCode: this.canvasStore.rawCouponCode?.toUpperCase(),
              currencyValue: this.appStore.profileCurrencySymbol,
            })
          },
        )
        response.headlines.forEach((newHeadline: string, index: number) => {
          if (
            newHeadline &&
            this.USkeywords.some((word) => newHeadline.toLowerCase().includes(word))
          ) {
            response.headlines[index] = this.handleUSToUKEnglish(response.headlines[index])
          }
        })
        if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
          this.canvasStore.aiGeneratedHeadlines = response.headlines
          this.editorStore.recentHeadlinePrompt = prompt
        } else if (textTypeName === TEXT_TYPE_NAMES.SUBHEADLINE) {
          this.canvasStore.aiGeneratedSubHeadlines = response.headlines
          this.editorStore.recentSubHeadlinePrompt = prompt
        }
      }
    } catch {
      this.canvasStore.showSnackbarMessage('Failed to generate headlines using AI!', 'error')
    } finally {
      if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
        this.editorStore.setWritingHeadlineUsingAI(false)
      } else {
        this.editorStore.setWritingSubheadlineUsingAI(false)
      }
    }
  }

  restoreCreatedBackdrops = (backdrops: any[]) => {
    this.editorStore.createdBackDrops = backdrops
  }

  restoreCreatedBackgrounds = (backgrounds: any[]) => {
    this.editorStore.createdBackGrounds = backgrounds
  }

  // Centralized loader handling
  startLoader() {
    this.canvasStore.setCanvasLoader(true)
    this.canvasStore.setHideLoader(false)
  }

  stopLoader() {
    this.canvasStore.setCanvasLoader(false)
    this.canvasStore.setHideLoader(true)
  }

  // Get character limits from active ad size config
  getCharLimits = (textTypeName: TextTypeName) => {
    if (textTypeName === TEXT_TYPE_NAMES.HEADLINE) {
      const limits = this.adSizeConfigStore.headlineCharLimits
      return { minChars: limits.min, maxChars: limits.max }
    } else {
      const discountEnabled = this.canvasStore.discountType !== discountTypes.none
      const limits = this.adSizeConfigStore.getSubheadlineCharLimits(discountEnabled)
      return { minChars: limits.min, maxChars: limits.max }
    }
  }

  // Returns the canvas JSON including Fabric.js custom properties
  getCanvasJsonWithCustomInfo(fabricCanvas: any) {
    if (!fabricCanvas) return
    const json = fabricCanvas.toJSON()

    json.objects.forEach((obj: any) => {
      if (obj.layerName === 'Headline') {
        const headlineObject = fabricCanvas
          .getObjects()
          .find((o: any) => o.layerName === 'Headline')
        obj.previousHeight = headlineObject?.previousHeight
      }
      if (obj.layerName === layerNames.AIShadow) {
        obj.selectable = false
        obj.evented = false
      }
    })

    return json
  }

  // Helper methods and computed properties
  private get upperCaseCouponCode() {
    return this.canvasStore.rawCouponCode?.toUpperCase() || ''
  }

  private get isDiscountEnabled() {
    return this.canvasStore.discountType !== 'None'
  }

  private handleUSToUKEnglish(text: string): string {
    // Simple US to UK English conversion
    return text
      .replace(/color/g, 'colour')
      .replace(/center/g, 'centre')
      .replace(/fiber/g, 'fibre')
      .replace(/meter/g, 'metre')
      .replace(/liter/g, 'litre')
      .replace(/theater/g, 'theatre')
      .replace(/caliber/g, 'calibre')
      .replace(/saber/g, 'sabre')
      .replace(/sepulcher/g, 'sepulchre')
      .replace(/defense/g, 'defence')
      .replace(/license/g, 'licence')
      .replace(/offense/g, 'offence')
      .replace(/pretense/g, 'pretence')
      .replace(/vise/g, 'vice')
      .replace(/analyze/g, 'analyse')
      .replace(/paralyze/g, 'paralyse')
      .replace(/tire/g, 'tyre')
      .replace(/organize/g, 'organise')
      .replace(/recognize/g, 'recognise')
      .replace(/apologize/g, 'apologise')
      .replace(/catalog/g, 'catalogue')
      .replace(/dialog/g, 'dialogue')
      .replace(/monolog/g, 'monologue')
      .replace(/prolog/g, 'prologue')
      .replace(/analog/g, 'analogue')
      .replace(/decal/g, 'decal')
      .replace(/fiscal/g, 'fiscal')
      .replace(/miscal/g, 'miscal')
      .replace(/recital/g, 'recital')
      .replace(/tidal/g, 'tidal')
      .replace(/arrival/g, 'arrival')
      .replace(/rival/g, 'rival')
      .replace(/vial/g, 'vial')
      .replace(/dial/g, 'dial')
      .replace(/rial/g, 'rial')
      .replace(/denial/g, 'denial')
      .replace(/memorial/g, 'memorial')
      .replace(/burial/g, 'burial')
      .replace(/trial/g, 'trial')
      .replace(/trivial/g, 'trivial')
      .replace(/advertise/g, 'advertise')
      .replace(/advise/g, 'advise')
      .replace(/devise/g, 'devise')
      .replace(/revise/g, 'revise')
      .replace(/supervise/g, 'supervise')
      .replace(/surmise/g, 'surmise')
      .replace(/televise/g, 'televise')
      .replace(/improvise/g, 'improvise')
      .replace(/compromise/g, 'compromise')
      .replace(/enterprise/g, 'enterprise')
      .replace(/exercise/g, 'exercise')
      .replace(/merchandise/g, 'merchandise')
      .replace(/surprise/g, 'surprise')
      .replace(/despise/g, 'despise')
      .replace(/disguise/g, 'disguise')
      .replace(/excise/g, 'excise')
      .replace(/incise/g, 'incise')
      .replace(/precise/g, 'precise')
      .replace(/concise/g, 'concise')
      .replace(/demise/g, 'demise')
      .replace(/emphasize/g, 'emphasise')
      .replace(/realize/g, 'realise')
      .replace(/specialize/g, 'specialise')
      .replace(/standardize/g, 'standardise')
      .replace(/summarize/g, 'summarise')
      .replace(/symbolize/g, 'symbolise')
      .replace(/utilize/g, 'utilise')
      .replace(/visualize/g, 'visualise')
      .replace(/authorize/g, 'authorise')
      .replace(/categorize/g, 'categorise')
      .replace(/characterize/g, 'characterise')
      .replace(/customize/g, 'customise')
      .replace(/dramatize/g, 'dramatise')
      .replace(/generalize/g, 'generalise')
      .replace(/globalize/g, 'globalise')
      .replace(/harmonize/g, 'harmonise')
      .replace(/humanize/g, 'humanise')
      .replace(/idealize/g, 'idealise')
      .replace(/legalize/g, 'legalise')
      .replace(/localize/g, 'localise')
      .replace(/maximize/g, 'maximise')
      .replace(/minimize/g, 'minimise')
      .replace(/modernize/g, 'modernise')
      .replace(/normalize/g, 'normalise')
      .replace(/optimize/g, 'optimise')
      .replace(/personalize/g, 'personalise')
      .replace(/popularize/g, 'popularise')
      .replace(/prioritize/g, 'prioritise')
      .replace(/rationalize/g, 'rationalise')
      .replace(/realize/g, 'realise')
      .replace(/recognize/g, 'recognise')
      .replace(/regularize/g, 'regularise')
      .replace(/reorganize/g, 'reorganise')
      .replace(/revitalize/g, 'revitalise')
      .replace(/stabilize/g, 'stabilise')
      .replace(/synchronize/g, 'synchronise')
      .replace(/systematize/g, 'systematise')
  }

  // Get optimal origin based on product count and position
  private getOptimalOrigin = () => {
    const originY = 'bottom' // Always bottom
    const originX = 'center'

    return { originX, originY }
  }

  // Adjust position when changing origin to maintain visual placement
  private adjustPositionForNewOrigin = (product: any, newOriginX: string, newOriginY: string) => {
    const currentWidth = product.getScaledWidth()
    const currentHeight = product.getScaledHeight()
    const angle = product.angle || 0
    const angleRad = (angle * Math.PI) / 180

    // Calculate offset differences
    let offsetX = 0
    let offsetY = 0

    // X offset calculation
    if (product.originX === 'left' && newOriginX === 'center') {
      offsetX = currentWidth / 2
    } else if (product.originX === 'left' && newOriginX === 'right') {
      offsetX = currentWidth
    } else if (product.originX === 'center' && newOriginX === 'left') {
      offsetX = -currentWidth / 2
    } else if (product.originX === 'center' && newOriginX === 'right') {
      offsetX = currentWidth / 2
    } else if (product.originX === 'right' && newOriginX === 'left') {
      offsetX = -currentWidth
    } else if (product.originX === 'right' && newOriginX === 'center') {
      offsetX = -currentWidth / 2
    }

    // Y offset calculation
    if (product.originY === 'top' && newOriginY === 'center') {
      offsetY = currentHeight / 2
    } else if (product.originY === 'top' && newOriginY === 'bottom') {
      offsetY = currentHeight
    } else if (product.originY === 'center' && newOriginY === 'top') {
      offsetY = -currentHeight / 2
    } else if (product.originY === 'center' && newOriginY === 'bottom') {
      offsetY = currentHeight / 2
    } else if (product.originY === 'bottom' && newOriginY === 'top') {
      offsetY = -currentHeight
    } else if (product.originY === 'bottom' && newOriginY === 'center') {
      offsetY = -currentHeight / 2
    }

    // Apply rotation transformation to the offset if object is rotated
    if (Math.abs(angle) > 0.01) {
      const rotatedOffsetX = offsetX * Math.cos(angleRad) - offsetY * Math.sin(angleRad)
      const rotatedOffsetY = offsetX * Math.sin(angleRad) + offsetY * Math.cos(angleRad)
      offsetX = rotatedOffsetX
      offsetY = rotatedOffsetY
    }

    // Apply position adjustments
    product.set({
      left: product.left + offsetX,
      top: product.top + offsetY,
      originX: newOriginX,
      originY: newOriginY,
    })

    product.setCoords()
  }

  generateBackdropAndBackground = async (isRegenerate: boolean = false) => {
    const colors = this.brandStore.colours
    const newSelectedTextureIndex =
      this.placementStore.selectedBackgroundImage === ''
        ? -1
        : this.placementStore.backgroundImages.findIndex(
          (img) => img === this.placementStore.selectedBackgroundImage,
        )

    const selectedAiImageIndex =
      this.placementStore.selectedAIBackgroundImage === ''
        ? -1
        : this.placementStore.aiBackgroundImages.findIndex(
          (img) => img === this.placementStore.selectedAIBackgroundImage,
        )
    const safeOverlayIndex =
      overlays.findIndex((overlay) => overlay === this.placementStore.selectedOverlayImage) ?? 0

    if (isRegenerate) {
      this.editorStore.createdBackDrops = []
      this.editorStore.createdBackGrounds = []
      this.lastRandomIndex = []
    }
    const fromEditor = this.editorStore.createdBackDrops.length === 0
    // If the newly selected texture is special texture (index 1 or 2), initialize specialTextureCount as 1, otherwise start from 0.
    const maxSpecialTextures = 3
    let specialTextureCount = newSelectedTextureIndex === 1 || newSelectedTextureIndex === 2 ? 1 : 0

    // --- Backdrops Section ---
    if (this.editorStore.createdBackDrops.length === 0) {
      const color = getContrastColor(this.brandStore.selectedColor, BACKDROP_TEXT_COLORS)
      const backDrop = {
        backgroundColor: this.brandStore.selectedColor,
        textColor: color,
        backgroundTextureIndex: newSelectedTextureIndex === -1 ? 3 : newSelectedTextureIndex,
        logoInfo: [],
      }
      if (this.specialTexturesIndex.includes(newSelectedTextureIndex)) {
        const nonSpecialIndex =
          Math.floor(Math.random() * (this.placementStore.backgroundImages.length - 3)) + 3
        this.editorStore.specialIndex.push({
          index: this.editorStore.createdBackDrops.length,
          specialIndex: newSelectedTextureIndex,
          normalIndex: nonSpecialIndex,
        })
      }
      this.editorStore.addBackDrops(backDrop)
      for (const color of colors) {
        if (this.brandStore.selectedColor != color) {
          let randomIndex
          const random = Math.random()
          if (this.specialPlacements.includes(this.placementStore.selectedPlacementOption)) {
            if (specialTextureCount >= maxSpecialTextures) {
              randomIndex = this.getNonRepeatingRandom(
                3,
                this.placementStore.backgroundImages.length - 1,
                this.lastRandomIndex,
              )
            } else {
              if (random < 0.25) {
                randomIndex = 1
                specialTextureCount++
              } else if (random < 0.5) {
                randomIndex = 2
                specialTextureCount++
              } else {
                randomIndex = this.getNonRepeatingRandom(
                  3,
                  this.placementStore.backgroundImages.length - 1,
                  this.lastRandomIndex,
                )
              }
            }
          } else {
            randomIndex = this.getNonRepeatingRandom(
              3,
              this.placementStore.backgroundImages.length - 1,
              this.lastRandomIndex,
            )
          }
          const textColor = getContrastColor(color, BACKDROP_TEXT_COLORS)
          const backdrop = {
            backgroundColor: color,
            textColor: textColor,
            backgroundTextureIndex: randomIndex,
            logoInfo: [],
          }
          if (this.specialTexturesIndex.includes(randomIndex)) {
            const nonSpecialIndex =
              Math.floor(Math.random() * (this.placementStore.backgroundImages.length - 3)) + 3
            this.editorStore.specialIndex.push({
              index: this.editorStore.createdBackDrops.length,
              specialIndex: randomIndex,
              normalIndex: nonSpecialIndex,
            })
          }
          this.editorStore.addBackDrops(backdrop)
        }
      }
    }

    // --- Backgrounds Section ---
    if (
      this.editorStore.createdBackGrounds.length === 0 &&
      this.placementStore.aiBackgroundImages.length > 0
    ) {
      this.lastRandomIndex = []
      const usedImageIndexes: number[] = []
      const usedOverlayIndexes: number[] = [safeOverlayIndex]
      if (selectedAiImageIndex !== -1) {
        usedImageIndexes.push(selectedAiImageIndex)
      }
      for (const color of colors) {
        const randomIndex = this.getNonRepeatingRandom(
          0,
          this.placementStore.aiBackgroundImages.length - 1,
          usedImageIndexes,
        )
        const randomOverlayIndex = this.getNonRepeatingRandom(
          0,
          overlays.length - 1,
          usedOverlayIndexes,
        )
        const background = {
          backgroundImageIndex: randomIndex,
          textColor: AI_BACKGROUND_TEXT_COLOR,
          backgroundTextureIndex: randomOverlayIndex,
          backgroundColor: '',
          logoInfo: [],
        }
        this.editorStore.prependBackGrounds(background)
      }
    }

    if (newSelectedTextureIndex != -1 && fromEditor) {
      this.editorStore.setSelectedBackgroundOption(1)
      this.editorStore.baseSelectedBackGroundOption = 1
      this.editorStore.selectedBackdrop = '#backdrop' + 0
      this.editorStore.selectedBackground = ''
      if (this.editorStore.selectedBackDropIndex === null) {
        this.editorStore.setSelectedBackDrop(0)
        this.editorStore.setSelectedBackGround(null)
      }
    } else if (fromEditor) {
      this.editorStore.prependBackGrounds({
        backgroundImageIndex: selectedAiImageIndex,
        textColor: this.canvasStore.textColor,
        backgroundTextureIndex: safeOverlayIndex,
        logoInfo: [],
        backgroundColor: '',
      })
      this.editorStore.setSelectedBackgroundOption(0)
      this.editorStore.baseSelectedBackGroundOption = 0
      this.editorStore.selectedBackground = '#background' + 0
      this.editorStore.selectedBackdrop = ''
      if (this.editorStore.selectedBackGroundIndex === null) {
        this.editorStore.setSelectedBackGround(0)
        this.editorStore.setSelectedBackDrop(null)
      }
    }
    if (fromEditor) {
      this.editorStore.baseSelectedBackDropIndex =
        this.editorStore.selectedBackDropIndex === null ? null : 0
      this.editorStore.baseBackDrops = this.editorStore.createdBackDrops.map((item) => ({
        ...item,
      }))
      this.editorStore.baseSelectedBackGroundIndex =
        this.editorStore.selectedBackGroundIndex === null ? null : 0
      this.editorStore.baseCreatedBackGrounds = this.editorStore.createdBackGrounds.map((item) => ({
        ...item,
      }))
      if (this.placementStore.selectedAIBackgroundImage != '') {
        this.brandStore.selectedColor =
          this.creativeStore.selectedCreative?.overlayColor || this.brandStore.colours[0]
        const background =
          this.editorStore.createdBackGrounds[this.editorStore.selectedBackGroundIndex ?? 0]
        background.backgroundColor = this.brandStore.selectedColor
      }
      this.brandStore.baseColor = this.brandStore.selectedColor
    }
  }

  // Creative Generation for
  generateCreative = async () => {
    try {
      if (!this.placementStore.selectedPublishedVariant || !this.placementStore.selectedPlacement) {
        return false
      }

      this.prepareProducts()
      this.startLoader()
      this.editorStore.setIsRegenerating(true)

      const productDataItems = await this.processProductData()
      this.editorStore.activeProductDataItems = productDataItems
      const isFoodCategory = isAllFoodCategories(this.editorStore.activeProducts)
      const requestData = this.buildGenerateRequest(isFoodCategory)
      const response = await this.creativeService.generateCreative(requestData)
      const primaryBrandColor = response.primaryColor
      const suggestedPlacement: any = await this.setupPlacementData(productDataItems)

      const responseJson = JSON.parse(response.canvasJson)
      const colors = [
        primaryBrandColor,
        ...response.backgroundColors,
        ...(this.brandStore.selectedBrand?.colours || []),
      ]
      this.brandStore.colours = colors
      this.brandStore.selectedColor = primaryBrandColor
      let textColor = AI_BACKGROUND_TEXT_COLOR // default for AI background image
      if (this.editorStore.selectedBackgroundOption !== 0) {
        textColor = getContrastColor(response.primaryColor, BACKDROP_TEXT_COLORS)
      }
      this.canvasStore.textColor = textColor
      const headlineText = responseJson.objects.find(
        (obj: any) => obj.layerName === 'Headline',
      ).text
      const subheadlineText = responseJson.objects.find(
        (obj: any) => obj.layerName === 'SubHeadline',
      ).text
      this.updateCanvasJsonText(headlineText, 'Headline', { shouldReflow: false, color: textColor })
      this.updateCanvasJsonText(subheadlineText, 'SubHeadline', {
        shouldReflow: false,
        color: textColor,
      })

      // Check the product counts
      const newProducts = (await Promise.all(
        productDataItems.map(async (p) => {
          const product: any = p.fabricImage
          if (p.productData) {
            product.isMultiple = p.productData.isMultiple
          }
          return product
        }),
      )) as FabricImage[]

      // load placements
      await loadPlacements(
        this.canvasStore.fabricCanvas,
        newProducts,
        suggestedPlacement.placement,
        suggestedPlacement.mainShadow,
        suggestedPlacement.lightDirection,
        0,
        (suggestedPlacement as any).aspectCounts,
      )

      const selectedBackgroundIndex = this.placementStore.backgroundImages.findIndex(
        (bg) => bg === this.placementStore.selectedBackgroundImage,
      )
      if (
        !this.specialPlacements.includes(suggestedPlacement.placement) &&
        this.specialTexturesIndex.includes(selectedBackgroundIndex)
      ) {
        this.placementStore.selectedBackgroundImage =
          this.placementStore.backgroundImages[
          Math.floor(Math.random() * (this.placementStore.backgroundImages.length - 3)) + 3
          ]
        this.placementStore.selectedAIBackgroundImage = ''
      }
      if (
        !this.specialPlacements.includes(suggestedPlacement.placement) &&
        this.editorStore.selectedBackgroundOption === 0 &&
        !(
          suggestedPlacement.placement == PLACEMENT_TYPES.CORNER &&
          newProducts.every((p: any) => p.aspectType == PRODUCT_ASPECT_TYPES.HORIZONTAL)
        )
      ) {
        this.placementStore.selectedBackgroundImage =
          this.placementStore.backgroundImages[
          Math.floor(Math.random() * (this.placementStore.backgroundImages.length - 3)) + 3
          ]
        this.placementStore.selectedAIBackgroundImage = ''
      }
      if (
        suggestedPlacement.placement == PLACEMENT_TYPES.CORNER &&
        newProducts.every((p: any) => p.aspectType == PRODUCT_ASPECT_TYPES.HORIZONTAL) &&
        this.specialTexturesIndex.includes(selectedBackgroundIndex)
      ) {
        this.placementStore.selectedAIBackgroundImage = ''
      }
      // generate background/backdrop and select the first backdrop
      await this.generateBackdropAndBackground(true)
      if (this.editorStore.selectedBackgroundOption === 0) {
        await this.selectNewBackground(0)
      } else {
        await this.selectNewBackDrop(0)
      }

      // update logo
      await this.updateLogoOnShuffling(false)

      // Update AI header and subheadline
      const headlinePrompt =
        this.canvasStore.discountType === 'None'
          ? 'Write a catchy headline which describes the brand, products very well'
          : 'Write a catchy headline which describes the brand, products along with discount information very well'
      const subHeadlinePrompt =
        this.canvasStore.discountType === 'None'
          ? 'A supporting line that adds more detail about the brand, products very well'
          : 'A supporting line that adds more detail about the brand, products along with discount information very well'

      if (this.canvasStore.aiGeneratedHeadlines.length) {
        await this.writeUsingAI(headlinePrompt, TEXT_TYPE_NAMES.HEADLINE)
      }
      if (this.canvasStore.aiGeneratedSubHeadlines.length) {
        await this.writeUsingAI(subHeadlinePrompt, TEXT_TYPE_NAMES.SUBHEADLINE)
      }

      // ⚠️ Do NOT replace this with fabricCanvas.toJSON()
      // Always use getCanvasJsonWithCustomInfo() to preserve custom properties in fabric objects
      // (e.g., previousHeight, headline/subheadline vertical offset position).
      // Directly calling .toJSON() will lose custom data and break the editor state.
      const finalCanvasJson = this.getCanvasJsonWithCustomInfo(this.canvasStore.fabricCanvas)

      // Update Button positions
      const defaultObjs = this.placementStore.selectedPublishedVariant.canvasJson.objects.filter(
        (obj: any) => obj.layerName.includes('CTA'),
      )
      const currentObjs = finalCanvasJson.objects.filter((obj: any) =>
        obj.layerName.includes('CTA'),
      )
      for (let i = 0; i < currentObjs.length; i++) {
        const currentObj = currentObjs[i]
        const defaultObj = defaultObjs[i]
        if (currentObj && defaultObj) {
          const objIndex = finalCanvasJson.objects.indexOf(currentObj)
          finalCanvasJson.objects[objIndex] = {
            ...currentObj,
            left: defaultObj.left,
            top: defaultObj.top,
            scaleX: defaultObj.scaleX,
            scaleY: defaultObj.scaleY,
            width: defaultObj.width,
            height: defaultObj.height,
          }
        }
      }
      // Update Button visibility
      updateCTA(
        this.canvasStore.fabricCanvas,
        finalCanvasJson,
        isFoodCategory,
        textColor,
        primaryBrandColor,
      )

      this.canvasStore.setCanvasJson(finalCanvasJson)
      this.finalizeStoreData(
        response,
        this.canvasStore.lastUsedDiscountInfo,
        finalCanvasJson,
        responseJson,
        textColor,
        isFoodCategory,
      )
      await this.syncShadowWithNewChanges(true)

      return true
    } catch (error) {
      console.error(error)
      this.canvasStore.showSnackbarMessage('Failed to generate creative', 'error')
      return false
    } finally {
      this.stopLoader()
      this.editorStore.setIsRegenerating(false)
    }
  }

  selectNewOverlay = async (index: number) => {
    this.canvasStore.setHideLoader(false)
    this.canvasStore.setCanvasLoader(true)
    const bgUrl = this.placementStore.selectedBackgroundImage
    const overlayUrl = autofixOverlays[index]

    this.placementStore.selectedOverlayImage = overlayUrl
    const color = this.brandStore.selectedColor
    const url = await this.creativeService.GenerateBackgroundWithOverlay(
      this.appStore.selectedBusinessProfileId,
      {
        brandName: this.brandStore.selectedBrand?.name || '',
        bgUrl,
        overlayUrl,
        color: color,
      } as GenerateBgWithOverlayRequest,
    )
    await this.addBackgroundImage(this.canvasStore.fabricCanvas, url)
    this.canvasStore.setCanvasLoader(false)
    this.canvasStore.setHideLoader(true)
  }

  selectNewTextColor = async (index: number) => {
    const textColor = this.autofixStore.autofixColors[index]
    this.canvasStore.textColor = textColor
    const backgroundColor = this.brandStore.selectedColor
    const layers = this.canvasStore.fabricCanvas.getObjects().reduce((acc: any, obj: any) => {
      acc[obj.layerName] = obj
      return acc
    }, {})
    const headlineObj = layers['Headline']
    if (headlineObj) {
      headlineObj.set('fill', textColor)
    }

    const subheadlineObj = layers['SubHeadline']
    if (subheadlineObj) {
      subheadlineObj.set('fill', textColor)
    }

    let layerObj = layers['CTA']
    if (layers['CTA - Order now'] && layers['CTA - Order now'].visible) {
      layerObj = layers['CTA - Order now']
    }
    const textboxObj = layerObj?._objects.find((obj: any) => obj.type === 'textbox')
    if (textboxObj) {
      let finalColor = textColor
      finalColor = this.getBackdropCTAColor(textColor, backgroundColor)
      textboxObj.set('fill', finalColor)
    }
    this.canvasStore.setLayers(layers)
    this.canvasStore.fabricCanvas.renderAll()
  }

  private prepareProducts = () => {
    // Create a copy of active products to avoid mutating the store directly
    const products = [...this.editorStore.activeProducts]
    // Update the store with the modified products
    this.editorStore.setActiveProducts(products)
  }

  private processProductData = async (): Promise<
    { productData: ProductData; fabricImage: FabricImage }[]
  > => {
    const productDataItems: { productData: ProductData; fabricImage: FabricImage }[] = []
    const updatedProducts = [...this.editorStore.activeProducts]

    for (let i = 0; i < updatedProducts.length; i++) {
      const product: any = updatedProducts[i]
      const dimensionsResult = await getProductDimensions(product.productUrl, i)
      if (dimensionsResult.fabricImage) {
        const productWithInfo: ProductWithInfo = {
          ...(product as any),
          productInfo: {
            productData: dimensionsResult.productData,
            fabricImage: dimensionsResult.fabricImage,
          },
        }
        if (product.metaData) {
          productWithInfo.productInfo.productData.isMultiple = JSON.parse(product.metaData)[
            'isMultiple'
          ]
        }

        productWithInfo.productInfo.fabricImage.set('productId', product.id)
        updatedProducts[i] = productWithInfo
        productDataItems.push(productWithInfo.productInfo)
      }
    }

    // Update the store with the enhanced products
    this.editorStore.setActiveProducts(updatedProducts)
    return productDataItems
  }

  private setupPlacementData = async (
    productDataItems: { productData: ProductData; fabricImage: FabricImage }[],
  ) => {
    this.placementStore.processedProductData = productDataItems
    const aspectCounts = countAspects(productDataItems.map((p) => p.productData))
    const availablePlacements = determineAvailablePlacements(
      productDataItems.length,
      aspectCounts,
      productDataItems.map((p) => p.productData),
    )
    this.placementStore.placementOptions = availablePlacements
    const suggestedPlacement = await suggestOptimalPlacement(
      productDataItems.map((p) => p.productData),
      availablePlacements,
    )
    this.placementStore.selectedPlacementOption = suggestedPlacement.placement
    this.placementStore.basePlacementType = suggestedPlacement.placement
    this.placementStore.defaultPlacement = suggestedPlacement as any
    this.productStore.setBaseProducts(this.editorStore.activeProducts)
    return suggestedPlacement
  }

  private finalizeStoreData = (
    response: any,
    currentDiscountInfo: any,
    existingCanvasJson: any,
    responseJson: any,
    textColor: string,
    isFoodCategory: boolean,
  ) => {
    if (this.placementStore.selectedAIBackgroundImage == '') {
      this.brandStore.setSelectedColor(response.primaryColor)
    } else {
      this.brandStore.selectedColor = response.primaryColor
    }
    this.brandStore.setBaseData(
      this.brandStore.selectedLogo,
      response.primaryColor,
      response.backgroundColors,
    )
    this.canvasStore.setTextColor(textColor)
    this.canvasStore.setEnableResetCanvas(
      true,
      existingCanvasJson,
      responseJson.backgroundImage?.src || '',
    )
    this.placementStore.baseBackgroundImage = this.placementStore.selectedBackgroundImage
    this.canvasStore.setBaseRawCouponCode(this.canvasStore.rawCouponCode?.toUpperCase())
    this.canvasStore.setLastUsedDiscountInfo(currentDiscountInfo)
    this.canvasStore.setBaseDiscountInfo(currentDiscountInfo)
    this.canvasStore.setAreAllEdibleProducts(isFoodCategory)
  }

  private buildGenerateRequest = (isFoodCategory: boolean = false): GenerateRequest => ({
    brandName: this.brandStore.selectedBrand?.name || '',
    logoUrl:
      this.brandStore.logos.findIndex((l) => l === this.brandStore.selectedLogo) >= 0 &&
        this.brandStore.selectedLogo
        ? this.brandStore.selectedLogo.logoUrl
        : '',
    productUrls: this.editorStore.activeProducts.map((product: any) => product.productUrl),
    canvasJson: JSON.stringify(this.placementStore.selectedPublishedVariant?.canvasJson),
    guidelinesData: JSON.stringify(this.placementStore.selectedPlacement?.creativeSetGuidelines),
    baseBgImageUrl: this.placementStore.backgroundImages[3],
    primaryColor: null as any,
    discountInfo: JSON.stringify(this.getCurrentDiscountInfo()),
    label: this.brandStore.activeLabel.label,
    brief: this.brandStore.activeLabel.brief,
    isFoodCategory: isFoodCategory,
  })

  private getCurrentDiscountInfo = () => ({
    isEnabled: this.canvasStore.discountType !== discountTypes.none,
    type: this.canvasStore.discountType,
    variation: this.canvasStore.discountVariation,
    value: this.canvasStore.discountValue,
    couponCode: this.canvasStore.rawCouponCode?.toUpperCase(),
    currencyValue: this.appStore.profileCurrencySymbol,
  })

  private getNonRepeatingRandom(min: number, max: number, tracker: number[]): number {
    let index
    do {
      index = Math.floor(Math.random() * (max - min + 1)) + min
    } while (tracker.includes(index) && max - min > 0) // avoid infinite loop

    tracker.push(index)

    // reset only after full cycle
    if (tracker.length === max - min + 1) {
      tracker.length = 0 // clear array
    }

    return index
  }
}

export default EditorService
